import { SiteUserAccess } from './SiteUserAccess'
import { TenantUserInviteDialog } from './TenantUserInviteDialog'
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import QRCode from 'qrcode'
import { FuseGridSurfaceShell } from '../chrome/FuseGridSurfaceShell'
import {
  ServerApiClient,
  type QuickBooksSyncScheduleEntry,
  type QuickBooksSyncScheduleListResponse,
  type ServerDatasource,
  type ServerExtractRun,
  type ServerExtractVersion,
  type ServerMfaFactor,
  type ServerProject,
  type ServerProjectPermissionRule,
  type ServerRefreshSchedule,
  type ServerTableauPublishedExtract,
  type ServerTotpEnrollmentResponse,
  type ServerSite,
  type ServerSiteMembership,
  type ServerSiteSummary,
  type ServerTenantUser,
} from '../../data/ServerApiClient'
import {
  clearDevImpersonationSession,
  startDevImpersonationSession,
} from '../../data/devImpersonationSession'
import { markDemoSession } from '../../data/demoSession'
import {
  passkeyAuthenticationErrorMessage,
  serializePasskeyAuthentication,
  serializePasskeyRegistration,
  toCredentialCreationOptions,
  toCredentialRequestOptions,
} from '../../utils/webauthn'
import { buildCfoRoutePath } from './cfo/cfoRoutes'
import { CfoBrandMark, navigateTo } from '../cfo-site/CfoSiteShared'
import { PlatformTenantsPage } from './PlatformTenantsPage'
import '../cfo-site/CfoSite.css'
import './cfoAdminShell.css'

type Props = {
  topInsetPx?: number
}

export const workbookLaunchPath = (
  siteSlug: string,
  workbook: { id: string; workbook_kind?: string | null; has_quickbooks_connection?: boolean | null },
): string => {
  const base = `/${encodeURIComponent(siteSlug)}/workbooks/${encodeURIComponent(workbook.id)}`
  // Workbooks with a QuickBooks connection open straight into the CFO Core
  // experience. The `workbook_kind` checks are a defensive fallback; the
  // authoritative signal is `has_quickbooks_connection` from the site summary.
  const kind = String(workbook.workbook_kind || '').trim().toLowerCase()
  const hasQuickBooks =
    workbook.has_quickbooks_connection === true ||
    kind.includes('quickbooks') ||
    kind.includes('qbo') ||
    kind === 'cfo' ||
    kind === 'quickbooks_cfo'
  if (hasQuickBooks) {
    return buildCfoRoutePath({ siteSlug, workbookId: workbook.id, page: 'home' })
  }
  return base
}

export const canUseSharedDemoForAuthNext = (nextPath: string): boolean => {
  const value = String(nextPath || '').trim()
  if (!value.startsWith('/') || value.startsWith('//')) return false
  try {
    const next = new URL(value, 'https://app.fusegrid.local')
    if (next.pathname === '/connect') return true // FE:C4 AI connector management moved to /connect
    return next.pathname === '/oauth/authorize' && Boolean(next.searchParams.get('request')?.trim())
  } catch {
    return false
  }
}

type SiteSection = 'home' | 'explore' | 'users' | 'groups' | 'projects' | 'extracts' | 'quickbooks-sync' | 'settings'

type AppRoute =
  | { kind: 'landing' }
  | { kind: 'platform' }
  | { kind: 'auth'; view: 'forgot_password' }
  | { kind: 'auth'; view: 'reset_password'; purpose: 'password_reset' | 'account_setup'; token: string }
  | { kind: 'tenant'; section: 'sites' | 'users' }
  | { kind: 'site'; siteSlug: string; section: SiteSection }

type PermissionEditorState = {
  project: ServerProject
  rules: ServerProjectPermissionRule[]
  saving: boolean
  error: string | null
}

type LoginMfaChallengeState = {
  challengeId: string
  methods: string[]
}

type SecurityPanelState = {
  open: boolean
  loading: boolean
  factors: ServerMfaFactor[]
  recoveryCodesRemaining: number
  generatedRecoveryCodes: string[]
  totpEnrollment: ServerTotpEnrollmentResponse | null
  totpCode: string
  error: string | null
  busy: boolean
}

type ExtractAdminDetails = {
  schedule: ServerRefreshSchedule | null
  runs: ServerExtractRun[]
  extracts: ServerExtractVersion[]
  error: string | null
}

type QuickBooksSyncScheduleDraft = {
  enabled: boolean
  job_kind: string
  schedule_cadence: string
  interval_seconds: string
  timezone: string
  next_run_at: string
  skip_cdc: boolean
  skip_exports: boolean
  force_refresh: boolean
}

let adminLocalDevSessionPromise: Promise<boolean> | null = null

function createInitialSecurityPanelState(): SecurityPanelState {
  return {
    open: false,
    loading: false,
    factors: [],
    recoveryCodesRemaining: 0,
    generatedRecoveryCodes: [],
    totpEnrollment: null,
    totpCode: '',
    error: null,
    busy: false,
  }
}

function parseRoute(pathnameRaw: string): AppRoute {
  const pathname = String(pathnameRaw || '').trim() || '/'
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] === 'admin' && parts[1] === 'tenants') return { kind: 'platform' }
  if (parts[0] === 'auth' && parts[1] === 'forgot-password') {
    return { kind: 'auth', view: 'forgot_password' }
  }
  if (
    parts[0] === 'auth' &&
    parts[1] === 'reset-password' &&
    (parts[2] === 'password_reset' || parts[2] === 'account_setup') &&
    String(parts[3] || '').trim()
  ) {
    return {
      kind: 'auth',
      view: 'reset_password',
      purpose: parts[2] as 'password_reset' | 'account_setup',
      token: decodeURIComponent(parts[3] || ''),
    }
  }
  if (parts[0] === 'admin' && (parts[1] === 'sites' || parts[1] === 'users')) {
    return { kind: 'tenant', section: parts[1] }
  }
  if (
    parts.length >= 2 &&
    ['home', 'explore', 'users', 'groups', 'projects', 'extracts', 'quickbooks-sync', 'settings'].includes(parts[1] || '')
  ) {
    return {
      kind: 'site',
      siteSlug: decodeURIComponent(parts[0] || ''),
      section: parts[1] as SiteSection,
    }
  }
  return { kind: 'landing' }
}

export const authNextRequiresDocumentNavigation = (nextPath: string): boolean => {
  const value = String(nextPath || '').trim()
  if (!value.startsWith('/') || value.startsWith('//')) return false
  try {
    const next = new URL(value, 'https://app.fusegrid.local')
    return next.pathname !== '/' && parseRoute(next.pathname).kind === 'landing'
  } catch {
    return false
  }
}

export const authNextPathFromSearch = (searchRaw: string): string => {
  try {
    const raw = String(new URLSearchParams(searchRaw || '').get('next') || '').trim()
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return ''
    if (raw.includes('\\') || raw.includes('\n') || raw.includes('\r')) return ''
    if (raw.startsWith('/login') || raw.startsWith('/auth/')) return ''
    return raw
  } catch {
    return ''
  }
}

/**
 * Resolve the destination for an authenticated visitor on the login route.
 *
 * `/login` is owned by the legacy admin shell so it can render the credential
 * form while signed out. Once authentication is valid, however, that shell's
 * generic landing page is not the product home: `/` is the CFO experiences
 * selector. Keep an explicit safe return path when one exists and otherwise
 * canonicalize the authenticated login route to the product home.
 */
export const authenticatedLoginDestinationPath = (pathnameRaw: string, searchRaw: string): string => {
  const pathname = String(pathnameRaw || '').trim().replace(/\/+$/, '') || '/'
  if (pathname !== '/login') return ''
  return authNextPathFromSearch(searchRaw) || '/'
}

function roleCanManageSite(role: string | null | undefined): boolean {
  const normalized = String(role || '').trim().toLowerCase()
  return normalized === 'site_admin_creator' || normalized === 'site_admin_explorer'
}

const SITE_ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'explorer_can_publish', label: 'Explorer can publish' },
  { value: 'creator', label: 'Creator' },
  { value: 'site_admin_explorer', label: 'Site admin explorer' },
  { value: 'site_admin_creator', label: 'Site admin creator' },
  { value: 'unlicensed', label: 'Unlicensed' },
] as const

function siteRoleLabel(role: string | null | undefined): string {
  const normalized = String(role || '').trim()
  return SITE_ROLE_OPTIONS.find((option) => option.value === normalized)?.label || normalized || 'Viewer'
}

function siteNav(siteSlug: string, section: SiteSection): string {
  return `/${encodeURIComponent(siteSlug)}/${section}`
}

function toDateTimeLocalValue(value: string | null | undefined): string {
  const normalized = String(value || '').trim()
  if (!normalized) return ''
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return ''
  const offsetMs = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

function fromDateTimeLocalValue(value: string): string | null {
  const normalized = String(value || '').trim()
  if (!normalized) return null
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function quickBooksScheduleDraftFor(row: QuickBooksSyncScheduleEntry): QuickBooksSyncScheduleDraft {
  const schedule = row.schedule || null
  return {
    enabled: schedule?.enabled ?? false,
    job_kind: schedule?.job_kind || 'incremental_refresh',
    schedule_cadence: schedule?.schedule_cadence || 'daily',
    interval_seconds: schedule?.interval_seconds != null ? String(schedule.interval_seconds) : '',
    timezone: schedule?.timezone || 'UTC',
    next_run_at: toDateTimeLocalValue(schedule?.next_run_at),
    skip_cdc: schedule?.skip_cdc ?? false,
    skip_exports: schedule?.skip_exports ?? false,
    force_refresh: schedule?.force_refresh ?? false,
  }
}

function dedupeSitesById(sites: ServerSite[]): ServerSite[] {
  const seen = new Set<string>()
  const out: ServerSite[] = []
  for (const site of sites) {
    const siteId = String(site?.id || '').trim()
    if (!siteId || seen.has(siteId)) continue
    seen.add(siteId)
    out.push(site)
  }
  return out
}

function slugifySiteName(raw: string): string {
  let out = ''
  let prevDash = false
  for (const ch of String(raw || '').trim()) {
    const mapped = /[a-z0-9]/i.test(ch) ? ch.toLowerCase() : '-'
    if (mapped === '-') {
      if (prevDash) continue
      prevDash = true
      out += mapped
    } else {
      prevDash = false
      out += mapped
    }
  }
  return out.replace(/^-+|-+$/g, '')
}

function topButtonStyle(primary = false): CSSProperties {
  return {
    borderRadius: 7,
    border: `1px solid ${primary ? 'var(--fg-green)' : 'var(--fg-shell-border)'}`,
    background: primary ? 'var(--fg-green)' : 'var(--fg-surface)',
    color: primary ? '#fff' : 'var(--fg-shell-text)',
    padding: '7px 11px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  }
}

const adminPanelStyle: CSSProperties = {
  background: 'var(--fg-surface)',
  border: '1px solid var(--fg-border)',
  borderRadius: 10,
  boxShadow: 'var(--fg-shadow-sm)',
}

const adminMetricCardStyle: CSSProperties = {
  ...adminPanelStyle,
  borderRadius: 8,
}

const adminRowStyle: CSSProperties = {
  border: '1px solid var(--fg-border-2)',
  borderRadius: 8,
}

const adminMutedTextStyle: CSSProperties = {
  color: 'var(--fg-muted)',
}

export function TableauAdminShell({ topInsetPx = 0 }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  const [route, setRoute] = useState<AppRoute>(() => parseRoute(typeof window !== 'undefined' ? window.location.pathname : '/'))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [authError, setAuthError] = useState<string | null>(null)
  const [mySites, setMySites] = useState<ServerSite[]>([])
  const [hasLoadedSiteAccess, setHasLoadedSiteAccess] = useState(false)
  const [adminSites, setAdminSites] = useState<ServerSite[] | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [platformRole, setPlatformRole] = useState<'founder' | 'admin' | 'sales' | null>(null)
  const [adminUsers, setAdminUsers] = useState<ServerTenantUser[] | null>(null)
  const [siteSummary, setSiteSummary] = useState<ServerSiteSummary | null>(null)
  const [siteUsers, setSiteUsers] = useState<ServerSiteMembership[] | null>(null)
  const [siteDatasources, setSiteDatasources] = useState<ServerDatasource[] | null>(null)
  const [extractDetailsById, setExtractDetailsById] = useState<Record<string, ExtractAdminDetails>>({})
  const [tableauPublishedExtracts, setTableauPublishedExtracts] = useState<ServerTableauPublishedExtract[] | null>(null)
  const [quickBooksSyncScheduleData, setQuickBooksSyncScheduleData] = useState<QuickBooksSyncScheduleListResponse | null>(null)
  const [quickBooksSyncScheduleDrafts, setQuickBooksSyncScheduleDrafts] = useState<Record<string, QuickBooksSyncScheduleDraft>>({})
  const [extractSearch, setExtractSearch] = useState('')
  const [extractScheduledOnly, setExtractScheduledOnly] = useState(true)
  const [extractFailedOnly, setExtractFailedOnly] = useState(false)
  const [permissions, setPermissions] = useState<PermissionEditorState | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [resetPasswordValue, setResetPasswordValue] = useState('')
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('')
  const [loginMfaChallenge, setLoginMfaChallenge] = useState<LoginMfaChallengeState | null>(null)
  const [loginPasskeyRequest, setLoginPasskeyRequest] = useState<{ challengeId: string; options: any } | null>(null)
  const [loginPasskeyPreparing, setLoginPasskeyPreparing] = useState(false)
  const [loginTotpCode, setLoginTotpCode] = useState('')
  const [loginRecoveryCode, setLoginRecoveryCode] = useState('')
  const [firstSiteName, setFirstSiteName] = useState('')
  const [firstSiteSlug, setFirstSiteSlug] = useState('')
  const [firstSiteSlugEdited, setFirstSiteSlugEdited] = useState(false)
  const [security, setSecurity] = useState<SecurityPanelState>(() => createInitialSecurityPanelState())
  const [totpQrCodeDataUrl, setTotpQrCodeDataUrl] = useState<string | null>(null)

  useEffect(() => {
    const challenge = loginMfaChallenge
    if (!challenge?.methods.includes('passkey')) {
      setLoginPasskeyRequest(null)
      setLoginPasskeyPreparing(false)
      return
    }

    let cancelled = false
    setLoginPasskeyRequest(null)
    setLoginPasskeyPreparing(true)
    api.startMfaPasskeyChallenge(challenge.challengeId)
      .then((started) => {
        if (!cancelled) setLoginPasskeyRequest({ challengeId: challenge.challengeId, options: started.options })
      })
      .catch((error) => {
        if (!cancelled) setAuthError(String(error?.message || error || 'Failed to prepare passkey verification'))
      })
      .finally(() => {
        if (!cancelled) setLoginPasskeyPreparing(false)
      })

    return () => { cancelled = true }
  }, [api, loginMfaChallenge])

  const navigate = useCallback((path: string) => {
    if (typeof window === 'undefined') return
    const nextPath = String(path || '').trim() || '/'
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (nextPath === current) return
    window.history.pushState({}, '', nextPath)
    try { window.dispatchEvent(new Event('fg_route_change')) } catch { }
    setRoute(parseRoute(nextPath))
  }, [])

  const readAuthNextPath = useCallback((): string => {
    if (typeof window === 'undefined') return ''
    return authNextPathFromSearch(window.location.search)
  }, [])

  const resetLoadedState = useCallback(() => {
    setMySites([])
    setHasLoadedSiteAccess(false)
    setAdminSites(null)
    setPlatformRole(null)
    setAdminUsers(null)
    setSiteSummary(null)
    setSiteUsers(null)
    setSiteDatasources(null)
    setExtractDetailsById({})
    setTableauPublishedExtracts(null)
    setQuickBooksSyncScheduleData(null)
    setQuickBooksSyncScheduleDrafts({})
  }, [])

  const resetSecurityPanel = useCallback(() => {
    setSecurity(createInitialSecurityPanelState())
  }, [])

  const resetFirstSiteDraft = useCallback(() => {
    setFirstSiteName('')
    setFirstSiteSlug('')
    setFirstSiteSlugEdited(false)
  }, [])

  const startLocalDevSession = useCallback(async () => {
    if (adminLocalDevSessionPromise) return await adminLocalDevSessionPromise
    setBusy(true)
    setError(null)
    setAuthError(null)
    const sessionPromise = (async () => {
      const out = await api.bootstrapLocalAuthSession()
      if (!out?.login?.access_token) {
        throw new Error('Local auth bootstrap returned no session token.')
      }
      setLoginMfaChallenge(null)
      setLoginTotpCode('')
      setLoginRecoveryCode('')
      setPassword('')
      resetSecurityPanel()
      resetLoadedState()
      return true
    })()
    adminLocalDevSessionPromise = sessionPromise
    try {
      return await sessionPromise
    } catch (e: any) {
      setAuthError(String(e?.message || e || 'Failed to start local dev session'))
      return false
    } finally {
      if (adminLocalDevSessionPromise === sessionPromise) {
        adminLocalDevSessionPromise = null
      }
      setBusy(false)
    }
  }, [api, resetLoadedState, resetSecurityPanel])

  const loadSiteExtractData = useCallback(async (siteId: string) => {
    const normalizedSiteId = String(siteId || '').trim()
    if (!normalizedSiteId) {
      setSiteDatasources([])
      setExtractDetailsById({})
      return
    }
    const allDatasources = await api.listDatasources()
    const scopedDatasources = (Array.isArray(allDatasources) ? allDatasources : [])
      .filter((datasource) => String(datasource?.site_id || '').trim() === normalizedSiteId)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    setSiteDatasources(scopedDatasources)
    const tableauExtractsPromise = api.tableauListPublishedExtracts()
      .then((value) => ({ ok: true as const, value }))
      .catch(() => ({ ok: false as const, value: [] as ServerTableauPublishedExtract[] }))
    const detailEntries = await Promise.all(
      scopedDatasources.map(async (datasource) => {
        const [scheduleResult, runsResult, extractsResult] = await Promise.allSettled([
          api.getScheduleOptional(datasource.id),
          api.listExtractRuns(datasource.id),
          api.listExtracts(datasource.id),
        ])
        const error =
          (scheduleResult.status === 'rejected' ? String(scheduleResult.reason?.message || scheduleResult.reason || '') : '') ||
          (runsResult.status === 'rejected' ? String(runsResult.reason?.message || runsResult.reason || '') : '') ||
          (extractsResult.status === 'rejected' ? String(extractsResult.reason?.message || extractsResult.reason || '') : '')
        return [datasource.id, {
          schedule: scheduleResult.status === 'fulfilled' ? scheduleResult.value : null,
          runs: runsResult.status === 'fulfilled' && Array.isArray(runsResult.value) ? runsResult.value : [],
          extracts: extractsResult.status === 'fulfilled' && Array.isArray(extractsResult.value) ? extractsResult.value : [],
          error: error || null,
        } satisfies ExtractAdminDetails] as const
      }),
    )
    setExtractDetailsById(Object.fromEntries(detailEntries))
    const tableauExtractsResult = await tableauExtractsPromise
    const tableauExtracts = tableauExtractsResult.ok ? tableauExtractsResult.value : []
    setTableauPublishedExtracts(
      (Array.isArray(tableauExtracts) ? tableauExtracts : []).sort((a, b) => {
        const left = new Date(String(b.updated_at || 0)).getTime()
        const right = new Date(String(a.updated_at || 0)).getTime()
        if (left !== right) return left - right
        return String(a.datasource_name || '').localeCompare(String(b.datasource_name || ''))
      }),
    )
  }, [api])

  const loadSiteQuickBooksSyncData = useCallback(async (siteSlug: string) => {
    const normalizedSiteSlug = String(siteSlug || '').trim()
    if (!normalizedSiteSlug) {
      setQuickBooksSyncScheduleData(null)
      setQuickBooksSyncScheduleDrafts({})
      return
    }
    const data = await api.listSiteQuickBooksSyncSchedules(normalizedSiteSlug)
    setQuickBooksSyncScheduleData(data)
    setQuickBooksSyncScheduleDrafts((prev) => {
      const next: Record<string, QuickBooksSyncScheduleDraft> = {}
      for (const row of data.connections || []) {
        next[row.connection_id] = prev[row.connection_id] || quickBooksScheduleDraftFor(row)
      }
      return next
    })
  }, [api])

  const describeError = useCallback((err: any, fallback: string) => {
    const message = String(err?.message || err || '').trim()
    if (!message) return fallback
    if (/\bfailed to fetch\b|networkerror|err_connection_refused/i.test(message)) {
      return `Could not reach the API server at ${api.baseUrl}.`
    }
    return message
  }, [api.baseUrl])

  const reportError = useCallback((err: any, fallback: string) => {
    const message = describeError(err, fallback)
    if (/\b401\b|\b403\b|invalid token/i.test(message)) {
      setAuthError(message)
    } else {
      setError(message)
    }
  }, [describeError])

  const runAction = useCallback(async (action: () => Promise<void>, fallback: string) => {
    setBusy(true)
    setError(null)
    setAuthError(null)
    try {
      await action()
    } catch (e: any) {
      reportError(e, fallback)
    } finally {
      setBusy(false)
    }
  }, [reportError])

  const applyDeletedWorkbook = useCallback((workbookId: string) => {
    const targetId = String(workbookId || '').trim()
    if (!targetId) return
    setSiteSummary((prev) => {
      if (!prev) return prev
      const deleted = prev.workbooks.find((workbook) => workbook.id === targetId)
      if (!deleted) return prev
      return {
        ...prev,
        site: {
          ...prev.site,
          workbook_count: Math.max(0, Number(prev.site.workbook_count || 0) - 1),
        },
        projects: prev.projects.map((project) => (
          project.id === deleted.project_id
            ? { ...project, workbook_count: Math.max(0, Number(project.workbook_count || 0) - 1) }
            : project
        )),
        workbooks: prev.workbooks.filter((workbook) => workbook.id !== targetId),
      }
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onPop = () => setRoute(parseRoute(window.location.pathname))
    const onAuthInvalid = () => {
      setAuthError('Your session expired. Log in again.')
      setLoginMfaChallenge(null)
      resetSecurityPanel()
      resetLoadedState()
      resetFirstSiteDraft()
    }
    window.addEventListener('popstate', onPop)
    window.addEventListener('fusegrid_api_auth_invalid', onAuthInvalid as EventListener)
    return () => {
      window.removeEventListener('popstate', onPop)
      window.removeEventListener('fusegrid_api_auth_invalid', onAuthInvalid as EventListener)
    }
  }, [resetFirstSiteDraft, resetLoadedState, resetSecurityPanel])

  useEffect(() => {
    const otpauthUri = String(security.totpEnrollment?.otpauth_uri || '').trim()
    if (!otpauthUri) {
      setTotpQrCodeDataUrl(null)
      return
    }
    let cancelled = false
    void QRCode.toDataURL(otpauthUri, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 192,
    })
      .then((dataUrl: string) => {
        if (!cancelled) setTotpQrCodeDataUrl(dataUrl)
      })
      .catch(() => {
        if (!cancelled) setTotpQrCodeDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [security.totpEnrollment])

  const refresh = useCallback(async (options?: { force?: boolean }) => {
    const force = options?.force === true
    if (route.kind === 'auth') {
      setBusy(false)
      setError(null)
      return
    }
    if (!api.getToken()) {
      if (api.localAuthBootstrapCandidate()) {
        const started = await startLocalDevSession()
        if (!started) return
      }
      if (!api.getToken()) {
        setAuthError(null)
        resetSecurityPanel()
        resetLoadedState()
        return
      }
    }
    setError(null)
    setAuthError(null)
    const loadedSiteSlug = siteSummary?.site?.slug || null
    const isSameSiteNavigation = !force && route.kind === 'site' && loadedSiteSlug === route.siteSlug
    const preserveCurrentSiteState = route.kind === 'site' && loadedSiteSlug === route.siteSlug
    if (isSameSiteNavigation) {
      if (route.section !== 'users' && route.section !== 'extracts' && route.section !== 'quickbooks-sync') {
        if (siteUsers) setSiteUsers(null)
        setBusy(false)
        return
      }
      if (route.section === 'users' && siteUsers) {
        setBusy(false)
        return
      }
      if (route.section === 'extracts' && siteDatasources) {
        setBusy(false)
        return
      }
      if (route.section === 'quickbooks-sync' && quickBooksSyncScheduleData) {
        setBusy(false)
        return
      }
      setBusy(true)
      try {
        if (route.section === 'users') {
          setSiteUsers(await api.listSiteUsers(route.siteSlug))
        } else if (route.section === 'quickbooks-sync') {
          await loadSiteQuickBooksSyncData(route.siteSlug)
        } else {
          await loadSiteExtractData(siteSummary?.site?.id || '')
        }
      } catch (e: any) {
        reportError(
          e,
          route.section === 'users'
            ? 'Failed to load site users'
            : route.section === 'quickbooks-sync'
              ? 'Failed to load QuickBooks sync schedules'
              : 'Failed to load site extract activity',
        )
      } finally {
        setBusy(false)
      }
      return
    }

    setBusy(true)
    if (!preserveCurrentSiteState) {
      resetLoadedState()
    }
    try {
      const [sites, platformAccess] = await Promise.all([
        api.listMySites(),
        api.getPlatformAccess().catch(() => ({ role: null })),
      ])
      setPlatformRole(platformAccess.role)
      const nextMySites = Array.isArray(sites) ? sites : []
      setHasLoadedSiteAccess(true)
      setMySites(nextMySites)

      // A standalone app can send an expired access token to /login?next=...
      // while the durable refresh session is still valid. listMySites() above
      // is the validation/refresh boundary; only after it succeeds is it safe
      // to honor the return path. Doing this before the authenticated request
      // would bounce the same expired token between the two apps forever.
      const restoredSessionDestination = route.kind === 'landing'
        ? authenticatedLoginDestinationPath(window.location.pathname, window.location.search)
        : ''
      if (restoredSessionDestination) {
        if (authNextRequiresDocumentNavigation(restoredSessionDestination)) {
          window.location.assign(restoredSessionDestination)
          return
        }
        navigate(restoredSessionDestination)
        return
      }

      const eagerSiteSummaryPromise =
        route.kind === 'site' && nextMySites.some((site) => site.slug === route.siteSlug)
          ? api.getSiteSummary(route.siteSlug)
          : null

      if (route.kind === 'landing' && nextMySites.length === 1 && !platformAccess.role) {
        const onlySlug = nextMySites[0].slug
        // CFO Core customers skip the site/admin experience and land directly
        // in their CFO Core. The authoritative signal is a QuickBooks-connected
        // workbook (a statement binding) on the site summary. Internal/Tableau
        // users (no such workbook) keep the existing site-home behavior.
        try {
          const summary = await api.getSiteSummary(onlySlug)
          const cfoWorkbook = (summary?.workbooks || []).find(
            (w) => w?.has_quickbooks_connection === true,
          )
          if (cfoWorkbook?.id) {
            navigate(buildCfoRoutePath({ siteSlug: onlySlug, workbookId: cfoWorkbook.id, page: 'home' }))
            return
          }
        } catch {
          /* fall through to the site home if the summary can't be loaded */
        }
        navigate(siteNav(onlySlug, 'home'))
        return
      }

      const tenantSitesResult = await api.listAdminSites()
        .then((v) => ({ ok: true as const, value: v, error: null }))
        .catch((error) => ({ ok: false as const, value: null, error }))
      const nextAdminSites = tenantSitesResult.ok && Array.isArray(tenantSitesResult.value) ? tenantSitesResult.value : []
      const reachableSites = dedupeSitesById([...nextMySites, ...nextAdminSites])
      setAdminSites(tenantSitesResult.ok ? nextAdminSites : null)

      if (route.kind === 'tenant') {
        if (!tenantSitesResult.ok) {
          reportError(tenantSitesResult.error, 'Failed to load tenant sites')
          return
        }
        if (route.section === 'users') {
          try {
            setAdminUsers(await api.listAdminUsers())
          } catch (e: any) {
            setAdminUsers(null)
            reportError(e, 'Failed to load tenant users')
          }
        }
      } else if (route.kind === 'site') {
        const routeSite = reachableSites.find((site) => site.slug === route.siteSlug) || null
        if (!routeSite) {
          setError(`Site "${route.siteSlug}" not found.`)
          return
        }
        const summary = eagerSiteSummaryPromise
          ? await eagerSiteSummaryPromise
          : await api.getSiteSummary(route.siteSlug)
        setSiteSummary(summary)
        if (route.section === 'users') {
          setSiteUsers(await api.listSiteUsers(route.siteSlug))
          setSiteDatasources(null)
          setExtractDetailsById({})
          setQuickBooksSyncScheduleData(null)
          setQuickBooksSyncScheduleDrafts({})
        } else if (route.section === 'extracts') {
          setSiteUsers(null)
          setQuickBooksSyncScheduleData(null)
          setQuickBooksSyncScheduleDrafts({})
          await loadSiteExtractData(summary.site.id)
        } else if (route.section === 'quickbooks-sync') {
          setSiteUsers(null)
          setSiteDatasources(null)
          setExtractDetailsById({})
          await loadSiteQuickBooksSyncData(route.siteSlug)
        } else {
          setSiteUsers(null)
          setSiteDatasources(null)
          setExtractDetailsById({})
          setQuickBooksSyncScheduleData(null)
          setQuickBooksSyncScheduleDrafts({})
        }
      } else {
        setSiteSummary(null)
        setSiteUsers(null)
        setSiteDatasources(null)
        setExtractDetailsById({})
        setQuickBooksSyncScheduleData(null)
        setQuickBooksSyncScheduleDrafts({})
      }
    } catch (e: any) {
      reportError(e, 'Failed to load admin shell')
    } finally {
      setBusy(false)
    }
  }, [api, loadSiteExtractData, loadSiteQuickBooksSyncData, navigate, quickBooksSyncScheduleData, reportError, resetLoadedState, resetSecurityPanel, route, siteDatasources, siteSummary, siteUsers, startLocalDevSession])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const finishAuthenticatedLogin = useCallback(async () => {
    const destinationPath = authenticatedLoginDestinationPath(window.location.pathname, window.location.search) || '/'
    if (authNextRequiresDocumentNavigation(destinationPath)) {
      window.location.assign(destinationPath)
      return
    }
    navigate(destinationPath)
  }, [navigate])

  const startTestAsUser = useCallback(async (user: ServerTenantUser) => {
    if (typeof window === 'undefined') return
    const originalToken = String(window.localStorage.getItem('fusegrid_api_token') || '').trim()
    const originalRefreshToken = String(window.localStorage.getItem('fusegrid_api_refresh_token') || '').trim()
    if (!originalToken || !originalRefreshToken) {
      throw new Error('Cannot start a test-user session without an active admin session.')
    }
    const login = await api.devImpersonateUser(user.user_id)
    if (login.status !== 'authenticated' || !login.access_token || !login.refresh_token) {
      throw new Error('Dev impersonation did not return an authenticated session.')
    }
    startDevImpersonationSession({
      originalToken,
      originalRefreshToken,
      targetUserId: user.user_id,
      targetEmail: user.email,
      startedAt: Date.now(),
    })
    resetLoadedState()
    resetSecurityPanel()
    setPermissions(null)
    navigate('/')
    await refresh({ force: true })
  }, [api, navigate, refresh, resetLoadedState, resetSecurityPanel])

  const loadSecurityState = useCallback(async () => {
    const securityState = await api.listMfaFactors()
    setSecurity((prev) => ({
      ...prev,
      loading: false,
      factors: Array.isArray(securityState.factors) ? securityState.factors : [],
      recoveryCodesRemaining: Number(securityState.recovery_codes_remaining || 0),
      error: null,
    }))
  }, [api])

  const openSecurityPanel = useCallback(async () => {
    setSecurity((prev) => ({ ...prev, open: true, loading: true, error: null }))
    try {
      await loadSecurityState()
    } catch (e: any) {
      setSecurity((prev) => ({
        ...prev,
        loading: false,
        error: String(e?.message || e || 'Failed to load security settings'),
      }))
    }
  }, [loadSecurityState])

  const runSecurityAction = useCallback(async (action: () => Promise<void>) => {
    setSecurity((prev) => ({ ...prev, busy: true, error: null }))
    try {
      await action()
    } catch (e: any) {
      setSecurity((prev) => ({
        ...prev,
        error: String(e?.message || e || 'Security action failed'),
      }))
    } finally {
      setSecurity((prev) => ({ ...prev, busy: false }))
    }
  }, [])

  const currentSite = siteSummary?.site ?? null
  const canManageSite = roleCanManageSite(currentSite?.current_site_role)
  const canUseDevImpersonation = api.localAuthBootstrapCandidate()
  const siteGroups = siteSummary?.groups || []
  const siteProjects = siteSummary?.projects || []
  const siteNameById = useMemo(() => {
    const out = new Map<string, string>()
    for (const site of [...mySites, ...(adminSites || []), ...(currentSite ? [currentSite] : [])]) {
      const siteId = String(site?.id || '').trim()
      const siteName = String(site?.name || '').trim()
      if (!siteId || !siteName || out.has(siteId)) continue
      out.set(siteId, siteName)
    }
    return out
  }, [adminSites, currentSite, mySites])
  const displaySiteName = useCallback((siteId: string | null | undefined, fallbackSlug?: string | null) => {
    const normalizedId = String(siteId || '').trim()
    if (normalizedId) {
      const matched = siteNameById.get(normalizedId)
      if (matched) return matched
    }
    const fallback = String(fallbackSlug || '').trim()
    return fallback || 'Site'
  }, [siteNameById])

  const openProjectPermissions = useCallback(async (project: ServerProject) => {
    setPermissions({ project, rules: [], saving: false, error: null })
    try {
      const rules = await api.listProjectPermissions(project.id)
      setPermissions({ project, rules, saving: false, error: null })
    } catch (e: any) {
      setPermissions({ project, rules: [], saving: false, error: String(e?.message || e || 'Failed to load permissions') })
    }
  }, [api])

  const saveProjectPermissions = useCallback(async () => {
    if (!permissions) return
    setPermissions((prev) => prev ? { ...prev, saving: true, error: null } : prev)
    try {
      const saved = await api.putProjectPermissions(
        permissions.project.id,
        permissions.rules.map((rule) => ({
          subject_kind: rule.subject_kind,
          subject_id: rule.subject_id,
          capabilities: rule.capabilities || {},
          is_project_leader: rule.is_project_leader,
        })),
      )
      setPermissions((prev) => prev ? { ...prev, rules: saved, saving: false, error: null } : prev)
    } catch (e: any) {
      setPermissions((prev) => prev ? { ...prev, saving: false, error: String(e?.message || e || 'Failed to save permissions') } : prev)
    }
  }, [api, permissions])

  const openForgotPassword = useCallback(() => {
    setAuthError(null)
    setError(null)
    setLoginMfaChallenge(null)
    resetSecurityPanel()
    setResetPasswordValue('')
    setResetPasswordConfirm('')
    navigate('/auth/forgot-password')
  }, [navigate, resetSecurityPanel])

  const signOut = useCallback(async () => {
    setBusy(true)
    setError(null)
    setAuthError(null)
    let signedOut = false
    try {
      await api.logout()
      signedOut = true
    } catch (e: any) {
      if (!api.getToken()) {
        signedOut = true
      } else {
        reportError(e, 'Failed to sign out')
      }
    } finally {
      setBusy(false)
    }
    if (!signedOut) return
    clearDevImpersonationSession()
    resetLoadedState()
    resetFirstSiteDraft()
    setLoginMfaChallenge(null)
    resetSecurityPanel()
    setPermissions(null)
    navigate('/')
  }, [api, navigate, reportError, resetFirstSiteDraft, resetLoadedState, resetSecurityPanel])

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '12px 13px',
    borderRadius: 10,
    border: '1px solid var(--fg-border, #e7e2d6)',
    background: 'var(--fg-surface, #fffdf8)',
    color: 'var(--fg-text, #1c1f26)',
    fontSize: 14,
    boxSizing: 'border-box',
    outlineColor: '#1b4965',
  }

  const selectStyle: CSSProperties = {
    ...inputStyle,
    padding: '7px 32px 7px 10px',
    minWidth: 180,
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--fg-shell-text)',
    background: 'var(--fg-surface)',
    cursor: 'pointer',
  }

  const showFirstSiteOnboarding =
    route.kind === 'landing' &&
    Boolean(api.getToken()) &&
    hasLoadedSiteAccess &&
    mySites.length === 0 && !platformRole

  const fmtTs = useCallback((value: string | null | undefined) => {
    const normalized = String(value || '').trim()
    if (!normalized) return '—'
    const date = new Date(normalized)
    if (Number.isNaN(date.getTime())) return normalized
    return date.toLocaleString()
  }, [])

  const fmtBytes = useCallback((value: number | null | undefined) => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return '—'
    if (value < 1024) return `${value} B`
    const units = ['KB', 'MB', 'GB', 'TB']
    let next = value / 1024
    let unitIndex = 0
    while (next >= 1024 && unitIndex < units.length - 1) {
      next /= 1024
      unitIndex += 1
    }
    return `${next.toFixed(next >= 10 ? 0 : 1)} ${units[unitIndex]}`
  }, [])

  const latestRunByDatasourceId = useMemo(() => {
    const out = new Map<string, ServerExtractRun | null>()
    for (const datasource of siteDatasources || []) {
      const runs = extractDetailsById[datasource.id]?.runs || []
      const sorted = [...runs].sort((a, b) => {
        const left = new Date(String(b.created_at || b.finished_at || b.started_at || 0)).getTime()
        const right = new Date(String(a.created_at || a.finished_at || a.started_at || 0)).getTime()
        return left - right
      })
      out.set(datasource.id, sorted[0] || null)
    }
    return out
  }, [extractDetailsById, siteDatasources])

  const filteredSiteDatasources = useMemo(() => {
    const search = extractSearch.trim().toLowerCase()
    const rows = (siteDatasources || []).filter((datasource) => {
      const detail = extractDetailsById[datasource.id]
      const schedule = detail?.schedule || null
      const latestRun = latestRunByDatasourceId.get(datasource.id) || null
      if (extractScheduledOnly && !schedule) return false
      if (extractFailedOnly && latestRun?.status !== 'failed') return false
      if (!search) return true
      const haystack = [
        datasource.name,
        datasource.kind,
        datasource.md_database,
        datasource.md_reference,
        datasource.external_table,
        datasource.external_sql,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ')
      return haystack.includes(search)
    })
    rows.sort((a, b) => {
      const nextA = extractDetailsById[a.id]?.schedule?.next_run_at
      const nextB = extractDetailsById[b.id]?.schedule?.next_run_at
      const hasA = !!String(nextA || '').trim()
      const hasB = !!String(nextB || '').trim()
      if (hasA && hasB) {
        return new Date(String(nextA)).getTime() - new Date(String(nextB)).getTime()
      }
      if (hasA) return -1
      if (hasB) return 1
      return String(a.name || '').localeCompare(String(b.name || ''))
    })
    return rows
  }, [extractDetailsById, extractFailedOnly, extractScheduledOnly, extractSearch, latestRunByDatasourceId, siteDatasources])

  const recentExtractRuns = useMemo(() => (
    filteredSiteDatasources
      .flatMap((datasource) => (extractDetailsById[datasource.id]?.runs || []).map((run) => ({ datasource, run })))
      .filter(({ run }) => !extractFailedOnly || run.status === 'failed')
      .sort((a, b) => {
        const left = new Date(String(b.run.created_at || b.run.finished_at || b.run.started_at || 0)).getTime()
        const right = new Date(String(a.run.created_at || a.run.finished_at || a.run.started_at || 0)).getTime()
        return left - right
      })
      .slice(0, 24)
  ), [extractDetailsById, extractFailedOnly, filteredSiteDatasources])

  const recentExtractVersions = useMemo(() => (
    filteredSiteDatasources
      .flatMap((datasource) => (extractDetailsById[datasource.id]?.extracts || []).map((extract) => ({ datasource, extract })))
      .sort((a, b) => {
        const left = new Date(String(b.extract.created_at || b.extract.updated_at || 0)).getTime()
        const right = new Date(String(a.extract.created_at || a.extract.updated_at || 0)).getTime()
        return left - right
      })
      .slice(0, 24)
  ), [extractDetailsById, filteredSiteDatasources])

  const allPublishedExtractVersions = useMemo(() => (
    filteredSiteDatasources
      .flatMap((datasource) => (extractDetailsById[datasource.id]?.extracts || []).map((extract) => ({ datasource, extract })))
      .sort((a, b) => {
        const left = new Date(String(b.extract.created_at || b.extract.updated_at || 0)).getTime()
        const right = new Date(String(a.extract.created_at || a.extract.updated_at || 0)).getTime()
        if (left !== right) return left - right
        return String(a.datasource.name || '').localeCompare(String(b.datasource.name || ''))
      })
  ), [extractDetailsById, filteredSiteDatasources])

  const extractScheduledCount = useMemo(() => (
    (siteDatasources || []).filter((datasource) => !!extractDetailsById[datasource.id]?.schedule).length
  ), [extractDetailsById, siteDatasources])

  const extractFailedCount = useMemo(() => (
    (siteDatasources || []).filter((datasource) => latestRunByDatasourceId.get(datasource.id)?.status === 'failed').length
  ), [latestRunByDatasourceId, siteDatasources])

  const extractVersionCount = useMemo(() => (
    (siteDatasources || []).reduce((sum, datasource) => sum + (extractDetailsById[datasource.id]?.extracts.length || 0), 0)
  ), [extractDetailsById, siteDatasources])

  const filteredTableauPublishedExtracts = useMemo(() => {
    const search = extractSearch.trim().toLowerCase()
    const rows = Array.isArray(tableauPublishedExtracts) ? tableauPublishedExtracts : []
    if (!search) return rows
    return rows.filter((extract) => (
      [
        extract.datasource_name,
        extract.datasource_key,
        extract.import_id,
        extract.storage_type,
        extract.engine,
        extract.extract_uri,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ')
        .includes(search)
    ))
  }, [extractSearch, tableauPublishedExtracts])

  const clientAuthMessage = (message: string): string => {
    const normalized = String(message || '').trim()
    if (/ExpiredSignature|Token verification failed|API 401/i.test(normalized)) {
      return 'Your session expired. Please sign in again.'
    }
    return normalized
  }

  const renderAuthMessage = () => (
    authError ? <div className="cfo-error">{clientAuthMessage(authError)}</div> : null
  )

  const renderAuthFrame = (children: ReactNode) => (
    <div className="cfo-site">
      <div className="cfo-center" style={{ paddingTop: `calc(${topInsetPx}px + 40px)` }}>
        {children}
      </div>
    </div>
  )

  const renderAuth = () => {
    if (route.kind === 'auth' && route.view === 'forgot_password') {
      return (
        <div className="cfo-panel">
          <CfoBrandMark sub="by FuseGrid" />
          <h1>Reset password</h1>
          <p className="sub">Enter your email and we&apos;ll send you a one-time password reset link.</p>
          {renderAuthMessage()}
          <div className="cfo-field">
            <label htmlFor="auth-forgot-email">Email address</label>
            <input
              id="auth-forgot-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
          <div className="cfo-form-actions" style={{ display: 'flex', gap: 10 }}>
            <button
              className="cfo-btn cfo-btn-primary"
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={async () => {
                setBusy(true)
                setAuthError(null)
                try {
                  await api.requestPasswordReset(email)
                  setAuthError('If the account exists, a reset link has been sent.')
                } catch (e: any) {
                  setAuthError(String(e?.message || e || 'Failed to request password reset'))
                } finally {
                  setBusy(false)
                }
              }}
              disabled={busy || !email.trim()}
            >
              Send reset link
            </button>
            <button className="cfo-btn cfo-btn-ghost" onClick={() => navigate('/')} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      )
    }
    if (route.kind === 'auth' && route.view === 'reset_password') {
      const isAccountSetup = route.purpose === 'account_setup'
      return (
        <div className="cfo-panel">
          <CfoBrandMark sub="by FuseGrid" />
          <h1>{isAccountSetup ? 'Set your password' : 'Choose a new password'}</h1>
          <p className="sub">
            {isAccountSetup
              ? 'Finish claiming your FuseGrid account by setting a password.'
              : 'Set a new password for your FuseGrid account.'}
          </p>
          {renderAuthMessage()}
          <div className="cfo-field">
            <label htmlFor="auth-reset-pw">New password</label>
            <input
              id="auth-reset-pw"
              value={resetPasswordValue}
              onChange={(e) => setResetPasswordValue(e.target.value)}
              type="password"
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </div>
          <div className="cfo-field">
            <label htmlFor="auth-reset-pw-confirm">Confirm new password</label>
            <input
              id="auth-reset-pw-confirm"
              value={resetPasswordConfirm}
              onChange={(e) => setResetPasswordConfirm(e.target.value)}
              type="password"
              autoComplete="new-password"
              placeholder="Re-enter password"
            />
          </div>
          <div className="cfo-form-actions" style={{ display: 'flex', gap: 10 }}>
            <button
              className="cfo-btn cfo-btn-primary"
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={async () => {
                setBusy(true)
                setAuthError(null)
                if (resetPasswordValue !== resetPasswordConfirm) {
                  setAuthError('Passwords do not match.')
                  setBusy(false)
                  return
                }
                try {
                  const completed = await api.completePasswordReset(route.token, route.purpose, resetPasswordValue)
                  setResetPasswordValue('')
                  setResetPasswordConfirm('')
                  if (completed.status === 'mfa_required' && completed.mfa_challenge_id) {
                    setLoginMfaChallenge({
                      challengeId: completed.mfa_challenge_id,
                      methods: Array.isArray(completed.mfa_allowed_methods) ? completed.mfa_allowed_methods : [],
                    })
                    setLoginTotpCode('')
                    setLoginRecoveryCode('')
                    navigate('/')
                    return
                  }
                  if (completed.status === 'mfa_enrollment_required') {
                    setAuthError('Xero-connected accounts require MFA or Sign in with Xero. Add an authenticator app or passkey to continue.')
                    navigate('/')
                    await openSecurityPanel()
                    return
                  }
                  const nextPath = String(completed.next_path || '').trim()
                  if (nextPath.startsWith('/') && !nextPath.startsWith('//')) {
                    window.location.assign(nextPath)
                    return
                  }
                  navigate('/')
                } catch (e: any) {
                  setAuthError(String(e?.message || e || 'Failed to reset password'))
                } finally {
                  setBusy(false)
                }
              }}
              disabled={busy || !resetPasswordValue.trim() || !resetPasswordConfirm.trim()}
            >
              {isAccountSetup ? 'Finish setup' : 'Reset password'}
            </button>
            <button className="cfo-btn cfo-btn-ghost" onClick={() => navigate('/')} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      )
    }
    if (loginMfaChallenge) {
      return (
        <div className="cfo-panel">
          <CfoBrandMark sub="by FuseGrid" />
          <h1>Verify your identity</h1>
          <p className="sub">Finish signing in with one of your enrolled MFA methods.</p>
          {renderAuthMessage()}
          {loginMfaChallenge.methods.includes('passkey') ? (
            <button
              className="cfo-btn cfo-btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
              onClick={async () => {
                const prepared = loginPasskeyRequest
                if (!prepared || prepared.challengeId !== loginMfaChallenge.challengeId) return
                // Invoke WebAuthn before any awaited work. Safari/WebKit can revoke
                // transient user activation after a network await, which causes a
                // misleading NotAllowedError even when the user selected a valid passkey.
                const credentialRequest = navigator.credentials.get(toCredentialRequestOptions(prepared.options))
                setBusy(true)
                setAuthError(null)
                try {
                  const credential = await credentialRequest
                  const out = await api.finishMfaPasskeyChallenge(
                    loginMfaChallenge.challengeId,
                    serializePasskeyAuthentication(credential),
                  )
                  if (out.status === 'authenticated') {
                    setLoginMfaChallenge(null)
                    setLoginTotpCode('')
                    setLoginRecoveryCode('')
                    await finishAuthenticatedLogin()
                  }
                } catch (e: any) {
                  setAuthError(passkeyAuthenticationErrorMessage(e))
                } finally {
                  setBusy(false)
                }
              }}
              disabled={busy || loginPasskeyPreparing || !loginPasskeyRequest || typeof window === 'undefined' || typeof window.PublicKeyCredential === 'undefined'}
            >
              {loginPasskeyPreparing ? 'Preparing passkey…' : 'Use passkey'}
            </button>
          ) : null}
          {loginMfaChallenge.methods.includes('totp') ? (
            <>
              <div className="cfo-field">
                <label htmlFor="auth-totp">Authenticator code</label>
                <input
                  id="auth-totp"
                  value={loginTotpCode}
                  onChange={(e) => setLoginTotpCode(e.target.value)}
                  inputMode="numeric"
                  placeholder="123456"
                />
              </div>
              <button
                className="cfo-btn cfo-btn-ghost"
                style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
                onClick={async () => {
                  setBusy(true)
                  setAuthError(null)
                  try {
                    const out = await api.verifyMfaTotp(loginMfaChallenge.challengeId, loginTotpCode)
                    if (out.status === 'authenticated') {
                      setLoginMfaChallenge(null)
                      setLoginTotpCode('')
                      setLoginRecoveryCode('')
                      await finishAuthenticatedLogin()
                    }
                  } catch (e: any) {
                    setAuthError(String(e?.message || e || 'Failed to verify code'))
                  } finally {
                    setBusy(false)
                  }
                }}
                disabled={busy || !loginTotpCode.trim()}
              >
                Verify authenticator code
              </button>
            </>
          ) : null}
          {loginMfaChallenge.methods.includes('recovery_code') ? (
            <>
              <div className="cfo-field">
                <label htmlFor="auth-recovery">Recovery code</label>
                <input
                  id="auth-recovery"
                  value={loginRecoveryCode}
                  onChange={(e) => setLoginRecoveryCode(e.target.value)}
                  placeholder="xxxx-xxxx-xxxx"
                />
              </div>
              <button
                className="cfo-btn cfo-btn-ghost"
                style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
                onClick={async () => {
                  setBusy(true)
                  setAuthError(null)
                  try {
                    const out = await api.verifyMfaRecoveryCode(loginMfaChallenge.challengeId, loginRecoveryCode)
                    if (out.status === 'authenticated') {
                      setLoginMfaChallenge(null)
                      setLoginTotpCode('')
                      setLoginRecoveryCode('')
                      await finishAuthenticatedLogin()
                    }
                  } catch (e: any) {
                    setAuthError(String(e?.message || e || 'Failed to verify recovery code'))
                  } finally {
                    setBusy(false)
                  }
                }}
                disabled={busy || !loginRecoveryCode.trim()}
              >
                Use recovery code
              </button>
            </>
          ) : null}
          <div className="cfo-altline">
            <button
              type="button"
              className="cfo-altlink"
              onClick={() => {
                if (busy) return
                api.setToken(null)
                setLoginMfaChallenge(null)
                setLoginTotpCode('')
                setLoginRecoveryCode('')
              }}
            >
              Back to sign in
            </button>
          </div>
        </div>
      )
    }
    return (
      <div className="cfo-panel">
        <CfoBrandMark sub="by FuseGrid" />
        <h1>Welcome back</h1>
        <p className="sub">Sign in to open your FuseGrid workspace.</p>
        {renderAuthMessage()}
        {canUseSharedDemoForAuthNext(readAuthNextPath()) ? (
          <section style={{ margin: '18px 0', padding: 16, border: '1px solid #b8d8c8', borderRadius: 14, background: 'linear-gradient(145deg, #f2fbf6, #f8fcfd)' }}>
            <div style={{ color: '#17613a', fontWeight: 850, fontSize: 16 }}>Try the shared synthetic demo</div>
            <p style={{ margin: '6px 0 12px', color: '#52636b', fontSize: 13, lineHeight: 1.5 }}>
              Connect your AI provider to sample companies with complete drill-through. No FuseGrid account or password required.
            </p>
            <button
              type="button"
              className="cfo-btn cfo-btn-primary"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={async () => {
                setBusy(true)
                setAuthError(null)
                try {
                  const out = await api.demoSession()
                  if (out.status !== 'authenticated' || !out.access_token) throw new Error('The shared FuseGrid demo is not available right now.')
                  markDemoSession(out.session_id)
                  await finishAuthenticatedLogin()
                } catch (e: any) {
                  setAuthError(String(e?.message || e || 'The shared FuseGrid demo is not available right now.'))
                } finally {
                  setBusy(false)
                }
              }}
              disabled={busy}
            >
              {busy ? 'Opening shared demo…' : 'Continue with shared demo'}
            </button>
            <div style={{ marginTop: 9, color: '#718087', fontSize: 11 }}>Synthetic data only · shared browser-saved views reset nightly</div>
          </section>
        ) : null}
        <div className="cfo-field">
          <label htmlFor="auth-email">Email address</label>
          <input
            id="auth-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
        </div>
        <div className="cfo-field">
          <label htmlFor="auth-password">Password</label>
          <input
            id="auth-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
          />
        </div>
        {api.localAuthBootstrapCandidate() ? (
          <button
            className="cfo-btn cfo-btn-ghost"
            style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
            onClick={async () => {
              const started = await startLocalDevSession()
              if (started) await finishAuthenticatedLogin()
            }}
            disabled={busy}
          >
            Use Local Dev Session
          </button>
        ) : null}
        <div className="cfo-form-actions" style={{ display: 'flex', gap: 10 }}>
          <button
            className="cfo-btn cfo-btn-primary"
            style={{ flex: 1, justifyContent: 'center' }}
            onClick={async () => {
              setBusy(true)
              setAuthError(null)
              try {
                const out = await api.login(email, password)
                if (out.status === 'mfa_required' && out.mfa_challenge_id) {
                  setLoginMfaChallenge({
                    challengeId: out.mfa_challenge_id,
                    methods: Array.isArray(out.mfa_allowed_methods) ? out.mfa_allowed_methods : [],
                  })
                  setPassword('')
                  setLoginTotpCode('')
                  setLoginRecoveryCode('')
                } else if (out.status === 'mfa_enrollment_required') {
                  setLoginMfaChallenge(null)
                  setAuthError('Xero-connected accounts require MFA or Sign in with Xero. Add an authenticator app or passkey to continue.')
                  await openSecurityPanel()
                } else {
                  setLoginMfaChallenge(null)
                  await finishAuthenticatedLogin()
                }
              } catch (e: any) {
                setAuthError(String(e?.message || e || 'Failed to log in'))
              } finally {
                setBusy(false)
              }
            }}
            disabled={busy || !email.trim() || !password.trim()}
          >
            Sign in
          </button>
          <button
            className="cfo-btn cfo-btn-ghost"
            style={{ flex: 1, justifyContent: 'center' }}
            onClick={() => navigateTo('/signup')}
            disabled={busy}
          >
            Create account
          </button>
        </div>
        <div className="cfo-altline">
          <button type="button" className="cfo-altlink" onClick={() => { if (!busy) openForgotPassword() }}>
            Forgot password?
          </button>
        </div>
      </div>
    )
  }

  const renderLanding = () => (
    <div style={{ padding: 20, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#1c1f26' }}>Select a Site</div>
          <div style={{ fontSize: 13, color: '#868c9a' }}>
            Choose the site you want to enter. Tenant administration stays separate from day-to-day site access.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {platformRole ? <button style={topButtonStyle()} onClick={() => navigate('/admin/tenants')}>All Tenants</button> : null}
          {adminSites ? (
            <>
              <button style={topButtonStyle()} onClick={() => navigate('/admin/sites')}>Tenant Sites</button>
              <button style={topButtonStyle()} onClick={() => navigate('/admin/users')}>Tenant Users</button>
              {canUseDevImpersonation ? (
                <button style={topButtonStyle(true)} onClick={() => navigate('/admin/users')}>View As User</button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      <div style={{ ...adminPanelStyle, padding: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1c1f26', marginBottom: 12 }}>Available Sites</div>
        <div style={{ display: 'grid', gap: 10 }}>
          {busy && !hasLoadedSiteAccess ? (
            <div style={{ fontSize: 12, color: '#868c9a' }}>
              Loading sites...
            </div>
          ) : showFirstSiteOnboarding ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ fontSize: 13, color: '#4c5360' }}>
                Create your first site to finish setting up this account.
              </div>
              <input
                value={firstSiteName}
                onChange={(e) => {
                  const nextName = e.target.value
                  setFirstSiteName(nextName)
                  if (!firstSiteSlugEdited) {
                    setFirstSiteSlug(slugifySiteName(nextName))
                  }
                }}
                placeholder="Site name"
                style={inputStyle}
              />
              <div style={{ display: 'grid', gap: 6 }}>
                <input
                  value={firstSiteSlug}
                  onChange={(e) => {
                    setFirstSiteSlugEdited(true)
                    setFirstSiteSlug(e.target.value)
                  }}
                  placeholder="Unique site URL"
                  style={inputStyle}
                />
                <div style={{ fontSize: 12, color: '#868c9a' }}>
                  Your site URL will be <code>/{slugifySiteName(firstSiteSlug) || 'your-site'}/home</code>.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={topButtonStyle(true)}
                  onClick={() => {
                    void runAction(async () => {
                      const normalizedSlug = slugifySiteName(firstSiteSlug)
                      const created = await api.createFirstSite({
                        name: firstSiteName.trim(),
                        slug: normalizedSlug || null,
                      })
                      resetFirstSiteDraft()
                      await refresh({ force: true })
                      navigate(siteNav(created.slug, 'home'))
                    }, 'Failed to create your first site')
                  }}
                  disabled={busy || !firstSiteName.trim() || !slugifySiteName(firstSiteSlug)}
                >
                  Create Site
                </button>
              </div>
            </div>
          ) : mySites.map((site) => (
            <div key={site.id} style={{ ...adminRowStyle, padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1c1f26' }}>{site.name}</div>
                <div style={{ fontSize: 12, ...adminMutedTextStyle }}>
                  {site.current_site_role || 'member'} · {site.user_count} users · {site.workbook_count} workbooks
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={topButtonStyle(true)} onClick={() => navigate(siteNav(site.slug, 'home'))}>Open Site</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {adminSites ? (
        <div style={{ ...adminPanelStyle, padding: 16, display: 'grid', gap: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1c1f26' }}>Cloud Administrator</div>
          <div style={{ fontSize: 12, ...adminMutedTextStyle }}>
            Tenant admins can manage sites, tenant users, and local-dev impersonation separately from the site picker.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={topButtonStyle()} onClick={() => navigate('/admin/sites')}>Manage Sites</button>
            <button style={topButtonStyle()} onClick={() => navigate('/admin/users')}>Manage Users</button>
            {canUseDevImpersonation ? (
              <button style={topButtonStyle(true)} onClick={() => navigate('/admin/users')}>View As User</button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )

  const renderTenantSites = () => (
    <div style={{ padding: 20, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#1c1f26' }}>Tenant Sites</div>
          <div style={{ fontSize: 12, color: '#868c9a' }}>Create, rename, and remove sites.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={topButtonStyle()} onClick={() => navigate('/')}>Back</button>
          <button
            style={topButtonStyle(true)}
            onClick={() => {
              const name = window.prompt('Site name')
              if (!name?.trim()) return
              void runAction(async () => {
                await api.createAdminSite({ name: name.trim() })
                await refresh({ force: true })
              }, 'Failed to create site')
            }}
          >
            New Site
          </button>
        </div>
      </div>
      {(adminSites || []).map((site) => (
        <div key={site.id} style={{ ...adminRowStyle, background: 'var(--fg-surface)', padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{site.name}</div>
            <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{site.user_count} users · {site.project_count} projects</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={topButtonStyle()} onClick={() => navigate(siteNav(site.slug, 'home'))}>Open Site</button>
            <button
              style={topButtonStyle()}
              onClick={() => {
                const nextName = window.prompt('Rename site', site.name)
                if (!nextName?.trim()) return
                void runAction(async () => {
                  await api.updateAdminSite(site.id, { name: nextName.trim() })
                  await refresh({ force: true })
                }, 'Failed to rename site')
              }}
            >
              Rename
            </button>
            <button
              style={topButtonStyle()}
              onClick={() => {
                if (!window.confirm(`Delete site "${site.name}"?`)) return
                void runAction(async () => {
                  await api.deleteAdminSite(site.id)
                  await refresh({ force: true })
                }, 'Failed to delete site')
              }}
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  )

  const renderTenantUsers = () => (
    <div style={{ padding: 20, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#1c1f26' }}>Tenant Users</div>
          <div style={{ fontSize: 12, color: '#868c9a' }}>Invite users and assign them to sites.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={topButtonStyle()} onClick={() => navigate('/')}>Back</button>
          <button style={topButtonStyle(true)} onClick={() => setInviteOpen(true)}>
            Invite User
          </button>
        </div>
      </div>
      {inviteOpen ? (
        <TenantUserInviteDialog
          api={api}
          onClose={() => setInviteOpen(false)}
          onInvited={async () => {
            setInviteOpen(false)
            await refresh({ force: true })
          }}
        />
      ) : null}
      {(adminUsers || []).map((user) => (
        <div key={user.user_id} style={{ ...adminRowStyle, background: 'var(--fg-surface)', padding: 12, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{user.full_name?.trim() || user.email}</div>
              {user.full_name?.trim() ? (
                <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{user.email}</div>
              ) : null}
              <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{user.tenant_admin ? 'Tenant admin' : 'Standard tenant user'}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {canUseDevImpersonation ? (
                <button
                  style={topButtonStyle()}
                  onClick={() => {
                    void runAction(async () => {
                      await startTestAsUser(user)
                    }, `Failed to view as ${user.email}`)
                  }}
                  disabled={busy}
                >
                  View As
                </button>
              ) : null}
              <button
                style={topButtonStyle()}
                onClick={() => {
                  const firstSite = (adminSites || [])[0]
                  void runAction(async () => {
                    await api.updateAdminUserMemberships(user.user_id, {
                      tenant_admin: !user.tenant_admin,
                      memberships: user.memberships.length > 0
                        ? user.memberships.map((m) => ({ site_id: m.site_id, site_role: m.site_role, status: m.status }))
                        : firstSite ? [{ site_id: firstSite.id, site_role: 'viewer', status: 'active' }] : [],
                    })
                    await refresh({ force: true })
                  }, 'Failed to update tenant user')
                }}
              >
                {user.tenant_admin ? 'Remove Tenant Admin' : 'Make Tenant Admin'}
              </button>
              <button
                style={topButtonStyle()}
                onClick={() => {
                  if (!window.confirm(`Remove ${user.email}?`)) return
                  void runAction(async () => {
                    await api.deleteAdminUser(user.user_id)
                    await refresh({ force: true })
                  }, 'Failed to remove tenant user')
                }}
              >
                Remove
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {user.memberships.map((membership) => (
              <div key={`${membership.site_id}:${membership.user_id}`} style={{ fontSize: 12, borderRadius: 999, background: '#efece4', padding: '4px 8px', color: '#4c5360' }}>
                {displaySiteName(membership.site_id, membership.site_slug)}: {membership.site_role} ({membership.status})
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )

  const renderSiteSidebar = () => {
    if (!currentSite || route.kind !== 'site') return null
    const items = [
      { section: 'home', label: 'Home' },
      { section: 'explore', label: 'Explore' },
      { section: 'users', label: 'Users' },
      { section: 'groups', label: 'Groups' },
      { section: 'projects', label: 'Projects' },
      { section: 'extracts', label: 'Extracts' },
      { section: 'quickbooks-sync', label: 'QuickBooks Sync' },
      { section: 'settings', label: 'Settings' },
    ] as const
    return (
      <aside className="fg-admin-sidebar" style={{ boxSizing: 'border-box' }}>
        <div className="fg-admin-sidebar-inner">
        <button style={{ ...topButtonStyle(), textAlign: 'left' }} onClick={() => navigate('/')}>All Sites</button>
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#868c9a', marginBottom: 4 }}>Site</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{currentSite.name}</div>
          <div style={{ fontSize: 12, color: '#868c9a' }}>{currentSite.current_site_role || 'member'}</div>
        </div>
        <div className="fg-admin-nav-list">
          {items.map((item) => (
            <button
              key={item.section}
              style={{
                ...topButtonStyle(route.section === item.section),
                textAlign: 'left',
                background: route.section === item.section ? '#1b4965' : '#fff',
                borderColor: route.section === item.section ? '#1b4965' : '#e7e2d6',
              }}
              onClick={() => navigate(siteNav(route.siteSlug, item.section))}
            >
              {item.label}
            </button>
          ))}
        </div>
        {adminSites ? (
          <div style={{ marginTop: 'auto', display: 'grid', gap: 6 }}>
            <button style={topButtonStyle()} onClick={() => navigate('/admin/sites')}>Tenant Sites</button>
            <button style={topButtonStyle()} onClick={() => navigate('/admin/users')}>Tenant Users</button>
          </div>
        ) : null}
        </div>
      </aside>
    )
  }

  const renderSiteContent = () => {
    if (route.kind !== 'site') return null
    if (!siteSummary) {
      if (busy) {
        return (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontSize: 24, fontWeight: 800 }}>Loading Site</div>
            <div style={{ fontSize: 13, color: '#868c9a' }}>Loading site details...</div>
          </div>
        )
      }
      return (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 24, fontWeight: 800 }}>Site Unavailable</div>
          <div style={{ fontSize: 13, color: '#868c9a' }}>
            This site route is invalid or the admin API is unavailable.
          </div>
        </div>
      )
    }
    if (route.section === 'home') {
      return (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800 }}>Welcome to {siteSummary.site.name}</div>
              <div style={{ fontSize: 13, color: '#868c9a' }}>
                {siteSummary.site.user_count} users, {siteSummary.site.project_count} projects, {siteSummary.site.workbook_count} visible workbooks.
              </div>
            </div>
            <button
              style={topButtonStyle(true)}
              onClick={() => {
                const name = window.prompt('Workbook name', 'Workbook')
                if (!name?.trim()) return
                void runAction(async () => {
                  const rootProject = siteSummary.projects.find((project) => project.parent_project_id == null) || siteSummary.projects[0]
                  const created = await api.createWorkbook({
                    name: name.trim(),
                    site_id: siteSummary.site.id,
                    project_id: rootProject?.id || null,
                  })
                  navigate(`/${encodeURIComponent(siteSummary.site.slug)}/workbooks/${encodeURIComponent(created.id)}`)
                }, 'Failed to create workbook')
              }}
              disabled={!siteSummary.site.current_site_role || !/creator|admin|publish/i.test(siteSummary.site.current_site_role)}
            >
              Create Workbook
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            {[
              ['Projects', siteSummary.projects.length],
              ['Groups', siteSummary.groups.length],
              ['Visible Workbooks', siteSummary.workbooks.length],
            ].map(([label, value]) => (
              <div key={label} style={{ ...adminMetricCardStyle, padding: 14 }}>
                <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, marginTop: 8 }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ ...adminPanelStyle, padding: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>Recent Workbooks</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {siteSummary.workbooks.map((workbook) => (
                <div key={workbook.id} style={{ ...adminRowStyle, padding: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{workbook.name}</div>
                    <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{workbook.project_name} · {workbook.role}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={topButtonStyle(true)} onClick={() => navigate(workbookLaunchPath(siteSummary.site.slug, workbook))}>Open</button>
                    {workbook.can_edit ? (
                      <>
                        <button
                          style={topButtonStyle()}
                          onClick={() => {
                            const name = window.prompt('Rename workbook', workbook.name)
                            if (!name?.trim() || name.trim() === workbook.name) return
                            void runAction(async () => {
                              await api.renameWorkbook(workbook.id, name.trim())
                              await refresh({ force: true })
                            }, 'Failed to rename workbook')
                          }}
                        >
                          Rename
                        </button>
                        <button
                          style={topButtonStyle()}
                          onClick={() => {
                            if (!window.confirm(`Delete workbook "${workbook.name}"?`)) return
                            void runAction(async () => {
                              await api.deleteWorkbook(workbook.id)
                              applyDeletedWorkbook(workbook.id)
                            }, 'Failed to delete workbook')
                          }}
                        >
                          Delete
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }
    if (route.section === 'explore' || route.section === 'projects') {
      return (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{route.section === 'explore' ? 'Explore' : 'Projects'}</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>Browse project hierarchy, move content, and manage project permissions.</div>
            </div>
            {canManageSite ? (
              <button
                style={topButtonStyle(true)}
                onClick={() => {
                  const name = window.prompt('Project name')
                  if (!name?.trim()) return
                  void runAction(async () => {
                    await api.createSiteProject(route.siteSlug, { name: name.trim() })
                    await refresh({ force: true })
                  }, 'Failed to create project')
                }}
              >
                New Project
              </button>
            ) : null}
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {siteProjects.map((project) => (
              <div key={project.id} style={{ ...adminPanelStyle, padding: 12, display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{project.name}</div>
                    <div style={{ fontSize: 12, ...adminMutedTextStyle }}>
                      {project.parent_project_id ? 'Nested project' : 'Top-level project'} · {project.asset_permission_mode} · {project.workbook_count} workbooks
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={topButtonStyle()} onClick={() => void openProjectPermissions(project)}>Permissions</button>
                    {canManageSite ? (
                      <>
                        <button
                          style={topButtonStyle()}
                          onClick={() => {
                            const name = window.prompt('Rename project', project.name)
                            if (!name?.trim()) return
                            void runAction(async () => {
                              await api.updateSiteProject(route.siteSlug, project.id, { name: name.trim() })
                              await refresh({ force: true })
                            }, 'Failed to rename project')
                          }}
                        >
                          Rename
                        </button>
                        <button
                          style={topButtonStyle()}
                          onClick={() => {
                            const parent = window.prompt('Move under parent project id (blank for top-level)', project.parent_project_id || '')
                            void runAction(async () => {
                              await api.moveSiteProject(route.siteSlug, project.id, parent?.trim() || null)
                              await refresh({ force: true })
                            }, 'Failed to move project')
                          }}
                        >
                          Move
                        </button>
                        <button
                          style={topButtonStyle()}
                          onClick={() => {
                            if (!window.confirm(`Delete project "${project.name}"?`)) return
                            void runAction(async () => {
                              await api.deleteSiteProject(route.siteSlug, project.id)
                              await refresh({ force: true })
                            }, 'Failed to delete project')
                          }}
                        >
                          Delete
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {siteSummary.workbooks.filter((workbook) => workbook.project_id === project.id).map((workbook) => (
                    <div key={workbook.id} style={{ borderTop: '1px solid var(--fg-border-2)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{workbook.name}</div>
                        <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{workbook.updated_at}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button style={topButtonStyle(true)} onClick={() => navigate(workbookLaunchPath(route.siteSlug, workbook))}>Open</button>
                        {workbook.can_edit ? (
                          <>
                            <button
                              style={topButtonStyle()}
                              onClick={() => {
                                const name = window.prompt('Rename workbook', workbook.name)
                                if (!name?.trim() || name.trim() === workbook.name) return
                                void runAction(async () => {
                                  await api.renameWorkbook(workbook.id, name.trim())
                                  await refresh({ force: true })
                                }, 'Failed to rename workbook')
                              }}
                            >
                              Rename
                            </button>
                            <button
                              style={topButtonStyle()}
                              onClick={() => {
                                if (!window.confirm(`Delete workbook "${workbook.name}"?`)) return
                                void runAction(async () => {
                                  await api.deleteWorkbook(workbook.id)
                                  applyDeletedWorkbook(workbook.id)
                                }, 'Failed to delete workbook')
                              }}
                            >
                              Delete
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }
    if (route.section === 'users') {
      return (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>Site Users</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>Active means site access is enabled. Setup and sign-in status are shown separately.</div>
            </div>
            {canManageSite ? (
              <button
                style={topButtonStyle(true)}
                onClick={() => {
                  const inviteEmail = window.prompt('User email')
                  if (!inviteEmail?.trim()) return
                  void runAction(async () => {
                    await api.createSiteUser(route.siteSlug, { email: inviteEmail.trim(), site_role: 'viewer' })
                    await refresh({ force: true })
                  }, 'Failed to add site user')
                }}
              >
                Add User
              </button>
            ) : null}
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {(siteUsers || []).map((member) => (
              <div key={member.user_id} style={{ ...adminPanelStyle, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  {/* A person's name leads, with the email beneath it — the
                      same shape the tenant list already uses. An account with
                      no name still shows its email rather than an empty row. */}
                  <div style={{ fontWeight: 700 }}>{member.full_name?.trim() || member.email}</div>
                  {member.full_name?.trim() ? (
                    <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{member.email}</div>
                  ) : null}
                  <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{siteRoleLabel(member.site_role)} · {member.status}</div>
                  <SiteUserAccess key={`${route.siteSlug}:${member.user_id}`} member={member}
                    generateLink={() => api.generateSiteUserSetupLink(route.siteSlug, member.user_id)} />
                </div>
                {canManageSite ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select
                      aria-label={`Role for ${member.email}`}
                      title="Site role"
                      value={SITE_ROLE_OPTIONS.some((option) => option.value === member.site_role) ? member.site_role : ''}
                      disabled={busy}
                      style={selectStyle}
                      onChange={(event) => {
                        const nextRole = event.currentTarget.value
                        if (!nextRole || nextRole === member.site_role) return
                        void runAction(async () => {
                          await api.updateSiteUser(route.siteSlug, member.user_id, { site_role: nextRole })
                          await refresh({ force: true })
                        }, 'Failed to update site user')
                      }}
                    >
                      {SITE_ROLE_OPTIONS.some((option) => option.value === member.site_role) ? null : (
                        <option value="">{siteRoleLabel(member.site_role)}</option>
                      )}
                      {SITE_ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <button
                      style={topButtonStyle()}
                      onClick={() => {
                        if (!window.confirm(`Remove ${member.email} from ${siteSummary.site.name}?`)) return
                        void runAction(async () => {
                          await api.deleteSiteUser(route.siteSlug, member.user_id)
                          await refresh({ force: true })
                        }, 'Failed to remove site user')
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )
    }
    if (route.section === 'groups') {
      return (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>Groups</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>Use groups to scale project permissions across site members.</div>
            </div>
            {canManageSite ? (
              <button
                style={topButtonStyle(true)}
                onClick={() => {
                  const name = window.prompt('Group name')
                  if (!name?.trim()) return
                  void runAction(async () => {
                    await api.createSiteGroup(route.siteSlug, { name: name.trim() })
                    await refresh({ force: true })
                  }, 'Failed to create group')
                }}
              >
                New Group
              </button>
            ) : null}
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {siteGroups.map((group) => (
              <div key={group.id} style={{ ...adminPanelStyle, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{group.name}</div>
                  <div style={{ fontSize: 12, ...adminMutedTextStyle }}>
                    {group.member_count} members{group.is_default_all_users ? ' · immutable All Users group' : ''}
                  </div>
                </div>
                {canManageSite && !group.is_default_all_users ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      style={topButtonStyle()}
                      onClick={() => {
                        const raw = window.prompt('Comma-separated member user ids', '')
                        const userIds = String(raw || '').split(',').map((value) => value.trim()).filter(Boolean)
                        void runAction(async () => {
                          await api.replaceSiteGroupMembers(route.siteSlug, group.id, userIds)
                          await refresh({ force: true })
                        }, 'Failed to update group members')
                      }}
                    >
                      Edit Members
                    </button>
                    <button
                      style={topButtonStyle()}
                      onClick={() => {
                        if (!window.confirm(`Delete group "${group.name}"?`)) return
                        void runAction(async () => {
                          await api.deleteSiteGroup(route.siteSlug, group.id)
                          await refresh({ force: true })
                        }, 'Failed to delete group')
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )
    }
    if (route.section === 'extracts') {
      return (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>Extract Activity</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>
                Review scheduled datasource refreshes, recent background runs, and published extract versions for this site.
              </div>
            </div>
            <button
              style={topButtonStyle()}
              onClick={() => void refresh({ force: true })}
              disabled={busy}
            >
              Refresh
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
            {[
              ['Scheduled Datasources', extractScheduledCount],
              ['Latest Failed Runs', extractFailedCount],
              ['Extract Versions', extractVersionCount],
              ['Tableau Published Extracts', filteredTableauPublishedExtracts.length],
            ].map(([label, value]) => (
              <div key={label} style={{ ...adminMetricCardStyle, padding: 14 }}>
                <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, marginTop: 8 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Filters</div>
                <div style={{ fontSize: 12, color: '#868c9a' }}>
                  Search applies to both extract systems. The schedule and failed-run checkboxes apply only to managed datasource refreshes.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  value={extractSearch}
                  onChange={(e) => setExtractSearch(e.target.value)}
                  placeholder="Search datasources"
                  style={{ ...inputStyle, minWidth: 220 }}
                />
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#4c5360' }}>
                  <input
                    type="checkbox"
                    checked={extractScheduledOnly}
                    onChange={(e) => setExtractScheduledOnly(e.target.checked)}
                  />
                  Scheduled only
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#4c5360' }}>
                  <input
                    type="checkbox"
                    checked={extractFailedOnly}
                    onChange={(e) => setExtractFailedOnly(e.target.checked)}
                  />
                  Failed latest run only
                </label>
              </div>
            </div>
          </div>

          <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Scheduled Refreshes</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>
                Datasources with the nearest next run are shown first. Unscheduled rows appear below when the filter is disabled.
              </div>
            </div>
            {!siteDatasources ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>Loading datasource refresh activity...</div>
            ) : filteredSiteDatasources.length === 0 ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>No datasources match the current filters.</div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {filteredSiteDatasources.map((datasource) => {
                  const detail = extractDetailsById[datasource.id]
                  const schedule = detail?.schedule || null
                  const latestRun = latestRunByDatasourceId.get(datasource.id) || null
                  const latestVersion = (detail?.extracts || []).slice().sort((a, b) => {
                    const left = new Date(String(b.created_at || b.updated_at || 0)).getTime()
                    const right = new Date(String(a.created_at || a.updated_at || 0)).getTime()
                    return left - right
                  })[0] || null
                  return (
                    <div key={datasource.id} style={{ ...adminRowStyle, padding: 12, display: 'grid', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 700 }}>{datasource.name}</div>
                          <div style={{ fontSize: 12, color: '#868c9a' }}>{datasource.kind} · {datasource.md_database || datasource.md_reference || datasource.external_table || 'Datasource'}</div>
                        </div>
                        <div style={{ fontSize: 12, color: schedule ? '#0e7c86' : '#868c9a', fontWeight: 700 }}>
                          {schedule ? (schedule.enabled ? 'Scheduled' : 'Disabled') : 'Unscheduled'}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                        <div style={{ fontSize: 12, color: '#4c5360' }}>
                          <strong>Schedule:</strong> {schedule ? `${schedule.schedule_type} ${schedule.schedule_value} ${schedule.timezone}` : '—'}
                          <div style={{ color: '#868c9a', marginTop: 4 }}>Next run: {fmtTs(schedule?.next_run_at)}</div>
                        </div>
                        <div style={{ fontSize: 12, color: '#4c5360' }}>
                          <strong>Latest run:</strong> {latestRun ? `${latestRun.status}${latestRun.reason ? ` · ${latestRun.reason}` : ''}` : '—'}
                          <div style={{ color: '#868c9a', marginTop: 4 }}>
                            Started {fmtTs(latestRun?.started_at || latestRun?.created_at)} · Finished {fmtTs(latestRun?.finished_at)}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#4c5360' }}>
                          <strong>Latest version:</strong> {latestVersion ? `v${latestVersion.version} · ${latestVersion.status}` : '—'}
                          <div style={{ color: '#868c9a', marginTop: 4 }}>
                            Created {fmtTs(latestVersion?.created_at || latestVersion?.updated_at)} · Size {fmtBytes(latestVersion?.bytes)}
                          </div>
                        </div>
                      </div>
                      {detail?.error ? <div style={{ fontSize: 12, color: '#c0492f' }}>{detail.error}</div> : null}
                      {latestRun?.error_message ? <div style={{ fontSize: 12, color: '#c0492f', whiteSpace: 'pre-wrap' }}>{latestRun.error_message}</div> : null}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: 16 }}>
            <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 10 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Recent Jobs</div>
                <div style={{ fontSize: 12, color: '#868c9a' }}>Latest extract runs across the datasources visible above.</div>
              </div>
              {recentExtractRuns.length === 0 ? (
                <div style={{ fontSize: 12, color: '#868c9a' }}>No extract runs recorded for the current filters.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {recentExtractRuns.map(({ datasource, run }) => (
                    <div key={run.id} style={{ ...adminRowStyle, padding: 10, display: 'grid', gap: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{datasource.name}</div>
                        <div style={{ fontSize: 11, color: '#868c9a' }}>{fmtTs(run.created_at)}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        {run.status}{run.reason ? ` · ${run.reason}` : ''} · rows {run.row_count ?? '—'} · bytes {fmtBytes(run.bytes)}
                      </div>
                      <div style={{ fontSize: 11, color: '#868c9a' }}>
                        started {fmtTs(run.started_at)} · finished {fmtTs(run.finished_at)}
                      </div>
                      {run.error_message ? <div style={{ fontSize: 11, color: '#c0492f', whiteSpace: 'pre-wrap' }}>{run.error_message}</div> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 10 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Extract History</div>
                <div style={{ fontSize: 12, color: '#868c9a' }}>Recently created extract versions for the visible datasources.</div>
              </div>
              {recentExtractVersions.length === 0 ? (
                <div style={{ fontSize: 12, color: '#868c9a' }}>No extract versions published for the current filters.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {recentExtractVersions.map(({ datasource, extract }) => (
                    <div key={extract.id} style={{ ...adminRowStyle, padding: 10, display: 'grid', gap: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{datasource.name}</div>
                        <div style={{ fontSize: 11, color: '#868c9a' }}>{fmtTs(extract.created_at || extract.updated_at)}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        v{extract.version} · {extract.status} · {fmtBytes(extract.bytes)}
                      </div>
                      <div style={{ fontSize: 11, color: '#868c9a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {extract.artifact_uri || 'No artifact URI recorded'}
                      </div>
                      {extract.error_message ? <div style={{ fontSize: 11, color: '#c0492f', whiteSpace: 'pre-wrap' }}>{extract.error_message}</div> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>All Managed Extract Versions</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>
                Full control-plane extract version catalog for the datasources currently visible in this admin view.
              </div>
            </div>
            {allPublishedExtractVersions.length === 0 ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>No managed extract versions found for the current filters.</div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {allPublishedExtractVersions.map(({ datasource, extract }) => (
                  <div key={`${datasource.id}:${extract.id}`} style={{ ...adminRowStyle, padding: 10, display: 'grid', gap: 6 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.3fr) minmax(120px, 0.8fr) minmax(140px, 0.9fr) minmax(160px, 1fr) minmax(160px, 1fr)', gap: 10, alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{datasource.name}</div>
                        <div style={{ fontSize: 11, color: '#868c9a' }}>{datasource.kind}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Version:</strong> v{extract.version}
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Status:</strong> {extract.status}
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Created:</strong> {fmtTs(extract.created_at || extract.updated_at)}
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Size:</strong> {fmtBytes(extract.bytes)}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#868c9a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {extract.artifact_uri || 'No artifact URI recorded'}
                    </div>
                    {extract.error_message ? <div style={{ fontSize: 11, color: '#c0492f', whiteSpace: 'pre-wrap' }}>{extract.error_message}</div> : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Tableau Published Extract Registry</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>
                Workbook-backed published extracts served from the Tableau registry path. This list is currently global rather than site-scoped.
              </div>
            </div>
            {!tableauPublishedExtracts ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>Loading Tableau published extracts...</div>
            ) : filteredTableauPublishedExtracts.length === 0 ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>No Tableau published extracts found for the current search.</div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {filteredTableauPublishedExtracts.map((extract) => (
                  <div key={`${extract.import_id}:${extract.datasource_key}`} style={{ ...adminRowStyle, padding: 10, display: 'grid', gap: 6 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.2fr) minmax(160px, 1fr) minmax(110px, 0.8fr) minmax(110px, 0.8fr) minmax(150px, 1fr)', gap: 10, alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{extract.datasource_name}</div>
                        <div style={{ fontSize: 11, color: '#868c9a' }}>{extract.datasource_key}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Import:</strong> {extract.import_id}
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Storage:</strong> {extract.storage_type}
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Engine:</strong> {extract.engine}
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Updated:</strong> {fmtTs(extract.updated_at)}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#4c5360' }}>
                      <strong>Rows:</strong> {extract.row_count ?? '—'}
                    </div>
                    <div style={{ fontSize: 11, color: '#868c9a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {extract.extract_uri || 'No extract URI recorded'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )
    }
    if (route.section === 'quickbooks-sync') {
      const rows = quickBooksSyncScheduleData?.connections || []
      const activeJobs = rows.filter((row) => row.active_job).length
      const scheduledRows = rows.filter((row) => row.schedule?.enabled).length
      const staleExports = rows.filter((row) => {
        if (!row.gl_exported_at) return true
        return Date.now() - new Date(row.gl_exported_at).getTime() > 3 * 24 * 60 * 60 * 1000
      }).length
      const updateDraft = (connectionId: string, patch: Partial<QuickBooksSyncScheduleDraft>) => {
        const sourceRow = rows.find((row) => row.connection_id === connectionId)
        if (!sourceRow) return
        setQuickBooksSyncScheduleDrafts((prev) => ({
          ...prev,
          [connectionId]: {
            ...(prev[connectionId] || quickBooksScheduleDraftFor(sourceRow)),
            ...patch,
          },
        }))
      }
      const saveSchedule = (row: QuickBooksSyncScheduleEntry) => {
        const draft = quickBooksSyncScheduleDrafts[row.connection_id] || quickBooksScheduleDraftFor(row)
        void runAction(async () => {
          const interval = Number.parseInt(draft.interval_seconds || '', 10)
          const data = await api.upsertSiteQuickBooksSyncSchedule(route.siteSlug, {
            connection_id: row.connection_id,
            enabled: draft.enabled,
            job_kind: draft.job_kind,
            schedule_cadence: draft.schedule_cadence,
            interval_seconds: Number.isFinite(interval) ? interval : null,
            timezone: draft.timezone || 'UTC',
            next_run_at: fromDateTimeLocalValue(draft.next_run_at),
            skip_cdc: draft.skip_cdc,
            skip_exports: draft.skip_exports,
            force_refresh: draft.force_refresh,
          })
          setQuickBooksSyncScheduleData(data)
          setQuickBooksSyncScheduleDrafts(Object.fromEntries(
            (data.connections || []).map((nextRow) => [nextRow.connection_id, quickBooksScheduleDraftFor(nextRow)]),
          ))
        }, 'Failed to save QuickBooks sync schedule')
      }
      const refreshNow = (row: QuickBooksSyncScheduleEntry) => {
        void runAction(async () => {
          const data = await api.triggerSiteQuickBooksIncrementalRefresh(route.siteSlug, row.connection_id)
          setQuickBooksSyncScheduleData(data)
          setQuickBooksSyncScheduleDrafts(Object.fromEntries(
            (data.connections || []).map((nextRow) => [nextRow.connection_id, quickBooksScheduleDraftFor(nextRow)]),
          ))
        }, 'Failed to start QuickBooks refresh')
      }
      return (
        <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 24, fontWeight: 800 }}>QuickBooks Sync</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>
                Manage connection-level sync schedules, export freshness, and durable job pickup for this site.
              </div>
            </div>
            <button style={topButtonStyle()} onClick={() => void loadSiteQuickBooksSyncData(route.siteSlug)} disabled={busy}>
              Refresh
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, minWidth: 0 }}>
            {[
              ['Active Connections', rows.length],
              ['Enabled Schedules', scheduledRows],
              ['Active Jobs', activeJobs],
              ['Stale GL Exports', staleExports],
            ].map(([label, value]) => (
              <div key={label} style={{ ...adminMetricCardStyle, padding: 14 }}>
                <div style={{ fontSize: 12, ...adminMutedTextStyle }}>{label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, marginTop: 8 }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 10, minWidth: 0 }}>
            {!quickBooksSyncScheduleData ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>Loading QuickBooks sync schedules...</div>
            ) : rows.length === 0 ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>No active QuickBooks connections are available for this site.</div>
            ) : (
              rows.map((row) => {
                const draft = quickBooksSyncScheduleDrafts[row.connection_id] || quickBooksScheduleDraftFor(row)
                const activeJob = row.active_job
                const lastJob = row.last_scheduled_job
                const canonicalGl = row.data_products?.find((product) => product.product_key === 'report:general_ledger')
                const canonicalTb = row.data_products?.find((product) => product.product_key === 'report:trial_balance')
                const glTbValidation = row.data_products?.find((product) => product.product_key === 'validation:gl_tb')
                return (
                  <div key={row.connection_id} style={{ ...adminRowStyle, padding: 12, display: 'grid', gap: 12, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', minWidth: 0 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800 }}>{row.company_name || row.realm_id}</div>
                        <div style={{ fontSize: 12, color: '#868c9a' }}>Realm {row.realm_id} · {row.environment} · {row.status}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: activeJob ? '#0e7c86' : '#868c9a', whiteSpace: 'nowrap' }}>
                          {activeJob ? `${activeJob.kind} ${activeJob.status}` : 'No active job'}
                        </div>
                        <button
                          style={topButtonStyle(true)}
                          onClick={() => refreshNow(row)}
                          disabled={busy || !row.can_manage || !!activeJob || row.status !== 'active'}
                        >
                          Refresh now
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Last sync:</strong> {fmtTs(row.last_sync_at)}
                        <div style={{ color: '#868c9a', marginTop: 4 }}>GL export: {fmtTs(row.gl_exported_at)} · rows {row.gl_export_rows ?? '—'}</div>
                        <div style={{ color: '#868c9a', marginTop: 4 }}>Canonical GL/TB: {fmtTs(canonicalGl?.fresh_at)} / {fmtTs(canonicalTb?.fresh_at)}</div>
                        <div style={{ color: '#868c9a', marginTop: 4 }}>Validation: {glTbValidation?.status || '—'} · {fmtTs(glTbValidation?.validated_at)}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Next run:</strong> {fmtTs(row.schedule?.next_run_at)}
                        <div style={{ color: '#868c9a', marginTop: 4 }}>Last scheduled: {fmtTs(row.schedule?.last_scheduled_at)}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Active job:</strong> {activeJob ? `${activeJob.status} · ${activeJob.triggered_by}` : '—'}
                        <div style={{ color: '#868c9a', marginTop: 4 }}>Started {fmtTs(activeJob?.started_at || activeJob?.created_at)}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#4c5360' }}>
                        <strong>Last job:</strong> {lastJob ? `${lastJob.kind} · ${lastJob.status}` : '—'}
                        <div style={{ color: '#868c9a', marginTop: 4 }}>Updated {fmtTs(lastJob?.updated_at)}</div>
                      </div>
                    </div>
                    {row.gl_export_error ? <div style={{ fontSize: 12, color: '#c0492f' }}>{row.gl_export_error}</div> : null}
                    {lastJob?.last_error ? <div style={{ fontSize: 12, color: '#c0492f' }}>{lastJob.last_error}</div> : null}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, alignItems: 'center', minWidth: 0 }}>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#4c5360', fontWeight: 700 }}>
                        <input type="checkbox" checked={draft.enabled} onChange={(event) => updateDraft(row.connection_id, { enabled: event.currentTarget.checked })} disabled={!row.can_manage} />
                        Enabled
                      </label>
                      <select value={draft.job_kind} onChange={(event) => updateDraft(row.connection_id, { job_kind: event.currentTarget.value })} style={{ ...selectStyle, minWidth: 0 }} disabled={!row.can_manage}>
                        <option value="incremental_refresh">Incremental</option>
                        <option value="full_repair">Full repair</option>
                        <option value="gl_repair">GL repair</option>
                        <option value="ar_aging_repair">A/R aging</option>
                        <option value="validation_only">Validation</option>
                      </select>
                      <select value={draft.schedule_cadence} onChange={(event) => updateDraft(row.connection_id, { schedule_cadence: event.currentTarget.value })} style={{ ...selectStyle, minWidth: 0 }} disabled={!row.can_manage}>
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="interval">Interval</option>
                      </select>
                      <input value={draft.interval_seconds} onChange={(event) => updateDraft(row.connection_id, { interval_seconds: event.currentTarget.value })} placeholder="Seconds" inputMode="numeric" style={{ ...inputStyle, minWidth: 0, padding: '7px 10px', fontSize: 12 }} disabled={!row.can_manage || draft.schedule_cadence !== 'interval'} />
                      <input value={draft.timezone} onChange={(event) => updateDraft(row.connection_id, { timezone: event.currentTarget.value })} placeholder="UTC" style={{ ...inputStyle, minWidth: 0, padding: '7px 10px', fontSize: 12 }} disabled={!row.can_manage} />
                      <input type="datetime-local" value={draft.next_run_at} onChange={(event) => updateDraft(row.connection_id, { next_run_at: event.currentTarget.value })} style={{ ...inputStyle, minWidth: 0, padding: '7px 10px', fontSize: 12 }} disabled={!row.can_manage} />
                      <button style={{ ...topButtonStyle(true), width: '100%' }} onClick={() => saveSchedule(row)} disabled={busy || !row.can_manage}>Save</button>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#4c5360' }}>
                        <input type="checkbox" checked={draft.skip_cdc} onChange={(event) => updateDraft(row.connection_id, { skip_cdc: event.currentTarget.checked })} disabled={!row.can_manage} />
                        Skip CDC
                      </label>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#4c5360' }}>
                        <input type="checkbox" checked={draft.skip_exports} onChange={(event) => updateDraft(row.connection_id, { skip_exports: event.currentTarget.checked })} disabled={!row.can_manage} />
                        Skip exports
                      </label>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#4c5360' }}>
                        <input type="checkbox" checked={draft.force_refresh} onChange={(event) => updateDraft(row.connection_id, { force_refresh: event.currentTarget.checked })} disabled={!row.can_manage} />
                        Force refresh
                      </label>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )
    }
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>Site Settings</div>
          <div style={{ fontSize: 12, color: '#868c9a' }}>Current site metadata and admin entry points.</div>
        </div>
        <div style={{ ...adminPanelStyle, padding: 16, display: 'grid', gap: 8 }}>
          <div><strong>Name:</strong> {siteSummary.site.name}</div>
          <div><strong>Role:</strong> {siteSummary.site.current_site_role || 'member'}</div>
          <div><strong>Projects:</strong> {siteSummary.projects.length}</div>
          <div><strong>Groups:</strong> {siteSummary.groups.length}</div>
        </div>
      </div>
    )
  }

  const renderSecurityPanel = () => {
    if (!security.open) return null
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(17, 24, 39, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box', zIndex: 60_000 }}>
        <div style={{ width: 720, maxWidth: '100%', maxHeight: '85vh', overflow: 'auto', ...adminPanelStyle, padding: 16, display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>Account Security</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>Manage passkeys, authenticator apps, recovery codes, and active sessions.</div>
            </div>
            <button
              style={topButtonStyle()}
              onClick={() => setSecurity((prev) => ({ ...prev, open: false, error: null, generatedRecoveryCodes: [], totpEnrollment: null, totpCode: '' }))}
            >
              Close
            </button>
          </div>
          {security.error ? <div style={{ fontSize: 12, color: '#c0492f' }}>{security.error}</div> : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              style={topButtonStyle(true)}
              onClick={() => void runSecurityAction(async () => {
                const started = await api.startPasskeyRegistration('Passkey')
                const credential = await navigator.credentials.create(toCredentialCreationOptions(started.options))
                await api.finishPasskeyRegistration(started.challenge_id, serializePasskeyRegistration(credential))
                await loadSecurityState()
              })}
              disabled={security.busy || security.loading || typeof window === 'undefined' || typeof window.PublicKeyCredential === 'undefined'}
            >
              Add Passkey
            </button>
            <button
              style={topButtonStyle()}
              onClick={() => void runSecurityAction(async () => {
                const enrollment = await api.startTotpEnrollment('Authenticator app')
                setSecurity((prev) => ({
                  ...prev,
                  totpEnrollment: enrollment,
                  totpCode: '',
                  generatedRecoveryCodes: [],
                }))
              })}
              disabled={security.busy || security.loading}
            >
              Add Authenticator App
            </button>
            <button
              style={topButtonStyle()}
              onClick={() => void runSecurityAction(async () => {
                const out = await api.regenerateRecoveryCodes()
                setSecurity((prev) => ({
                  ...prev,
                  generatedRecoveryCodes: out.codes || [],
                  recoveryCodesRemaining: Number(out.recovery_codes_remaining || 0),
                }))
                await loadSecurityState()
              })}
              disabled={security.busy || security.loading}
            >
              Regenerate Recovery Codes
            </button>
            <button
              style={topButtonStyle()}
              onClick={() => void runSecurityAction(async () => {
                await api.logoutAll()
                resetLoadedState()
                navigate('/')
              })}
              disabled={security.busy || security.loading}
            >
              Sign Out All Sessions
            </button>
          </div>
          <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Enrolled Factors</div>
            {security.loading ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>Loading security settings...</div>
            ) : security.factors.length === 0 ? (
              <div style={{ fontSize: 12, color: '#868c9a' }}>No MFA factors enrolled yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {security.factors.map((factor) => (
                  <div key={factor.id} style={{ ...adminRowStyle, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{factor.display_name}</div>
                      <div style={{ fontSize: 12, color: '#868c9a' }}>
                        {factor.factor_type} · last used {factor.last_used_at || 'never'}
                      </div>
                    </div>
                    <button
                      style={topButtonStyle()}
                      onClick={() => void runSecurityAction(async () => {
                        await api.disableMfaFactor(factor.id)
                        await loadSecurityState()
                      })}
                      disabled={security.busy}
                    >
                      Disable
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Recovery Codes</div>
            <div style={{ fontSize: 12, color: '#868c9a' }}>
              {security.recoveryCodesRemaining} codes remaining. Regenerate them any time to invalidate older backups.
            </div>
            {security.generatedRecoveryCodes.length > 0 ? (
              <div style={{ border: '1px dashed #d1d5db', borderRadius: 10, padding: 12, display: 'grid', gap: 6 }}>
                <div style={{ fontSize: 12, color: '#868c9a' }}>Store these one-time codes somewhere safe.</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                  {security.generatedRecoveryCodes.map((code) => (
                    <code key={code} style={{ fontSize: 13 }}>{code}</code>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          {security.totpEnrollment ? (
            <div style={{ ...adminPanelStyle, padding: 14, display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Confirm Authenticator App</div>
              <div style={{ fontSize: 12, color: '#868c9a' }}>Scan this QR code with your authenticator app, or enter the secret manually, then provide the current six-digit code to finish enrollment.</div>
              {totpQrCodeDataUrl ? (
                <div style={{ display: 'grid', justifyContent: 'start', gap: 6 }}>
                  <img
                    src={totpQrCodeDataUrl}
                    alt="Authenticator app QR code"
                    style={{ width: 192, height: 192, ...adminRowStyle, background: 'var(--fg-surface)', padding: 8 }}
                  />
                  <div style={{ fontSize: 12, color: '#868c9a' }}>Scan in Google Authenticator, 1Password, or another TOTP app.</div>
                </div>
              ) : null}
              <div><strong>Secret:</strong> <code>{security.totpEnrollment.secret}</code></div>
              <div style={{ fontSize: 12, color: '#868c9a', wordBreak: 'break-all' }}>{security.totpEnrollment.otpauth_uri}</div>
              <input
                value={security.totpCode}
                onChange={(e) => setSecurity((prev) => ({ ...prev, totpCode: e.target.value }))}
                inputMode="numeric"
                placeholder="123456"
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={topButtonStyle(true)}
                  onClick={() => void runSecurityAction(async () => {
                    const out = await api.confirmTotpEnrollment(security.totpEnrollment!.factor.id, security.totpCode)
                    setSecurity((prev) => ({
                      ...prev,
                      generatedRecoveryCodes: out.codes || [],
                      recoveryCodesRemaining: Number(out.recovery_codes_remaining || 0),
                      totpEnrollment: null,
                      totpCode: '',
                    }))
                    await loadSecurityState()
                  })}
                  disabled={security.busy || !security.totpCode.trim()}
                >
                  Confirm Authenticator App
                </button>
                <button
                  style={topButtonStyle()}
                  onClick={() => setSecurity((prev) => ({ ...prev, totpEnrollment: null, totpCode: '' }))}
                  disabled={security.busy}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  if (route.kind === 'auth') {
    return renderAuthFrame(renderAuth())
  }

  if (!api.getToken()) {
    return renderAuthFrame(renderAuth())
  }

  const activeShellSiteSlug = currentSite?.slug || siteSummary?.site.slug || ''
  const activeSiteHomeHref = route.kind === 'site'
    ? siteNav(route.siteSlug, 'home')
    : activeShellSiteSlug
      ? siteNav(activeShellSiteSlug, 'home')
      : '/'
  const shellTitle = route.kind === 'site'
    ? currentSite?.name || siteSummary?.site.name || route.siteSlug
    : route.kind === 'platform'
      ? 'All Tenants'
      : route.kind === 'tenant'
        ? route.section === 'sites' ? 'Tenant Sites' : 'Tenant Users'
        : 'FuseGrid'
  const shellSubtitle = route.kind === 'site'
    ? currentSite?.current_site_role || siteSummary?.site.current_site_role || 'Site workspace'
    : route.kind === 'platform'
      ? 'Platform administration'
      : route.kind === 'tenant'
        ? 'Tenant administration'
        : 'Choose a site or create your first workspace'

  return (
    <FuseGridSurfaceShell
      className="fg-admin-shell cfo-admin"
      title={shellTitle}
      subtitle={shellSubtitle}
      topInsetPx={topInsetPx}
      brand={(
        <button
          type="button"
          className="cfo-admin-brand"
          title="Go to home"
          aria-label="FuseGrid home"
          onClick={() => navigate(activeSiteHomeHref)}
        >
          <span className="cfo-admin-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
              <path
                d="M4 16 9 10l3.5 3L20 5"
                style={{ stroke: '#fff', strokeWidth: 2, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' }}
              />
              <circle cx="20" cy="5" r="2" fill="#fff" />
            </svg>
          </span>
          <span className="cfo-admin-brand-text">
            <b>CFO Core</b>
            <span>by FuseGrid</span>
          </span>
        </button>
      )}
      onBrandClick={() => navigate(activeSiteHomeHref)}
      sidebar={route.kind === 'site' ? renderSiteSidebar() : undefined}
      actions={(
        <>
          {platformRole && route.kind !== 'platform' ? <button title="All tenants" style={topButtonStyle()} onClick={() => navigate('/admin/tenants')}>All Tenants</button> : null}
          <button title="Account security" style={topButtonStyle()} onClick={() => void openSecurityPanel()} disabled={busy}>Security</button>
          <button title="Reset password" style={topButtonStyle()} onClick={openForgotPassword} disabled={busy}>Reset Password</button>
          <button title="Sign out" style={topButtonStyle()} onClick={() => void signOut()} disabled={busy}>Sign Out</button>
        </>
      )}
    >
      {error ? (
        <div style={{ margin: '12px 16px 0', border: '1px solid #efd7cd', background: '#f8e8e2', color: '#c0492f', borderRadius: 10, padding: '10px 12px', fontSize: 12 }}>
          {error}
        </div>
      ) : null}
      {route.kind === 'landing' ? renderLanding() : null}
      {route.kind === 'platform' ? (platformRole ? (
        <PlatformTenantsPage
          api={api}
          role={platformRole}
          onBack={() => navigate('/')}
          onOpenSite={(slug) => navigate(siteNav(slug, 'home'))}
          onAccessChanged={async () => { await refresh({ force: true }) }}
        />
      ) : <div style={{ padding: 20 }}>Platform staff access required.</div>) : null}
      {route.kind === 'tenant' && route.section === 'sites' ? renderTenantSites() : null}
      {route.kind === 'tenant' && route.section === 'users' ? renderTenantUsers() : null}
      {route.kind === 'site' ? renderSiteContent() : null}
      {permissions ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17, 24, 39, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box' }}>
          <div style={{ width: 820, maxWidth: '100%', maxHeight: '85vh', overflow: 'auto', ...adminPanelStyle, padding: 16, display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>Project Permissions</div>
                <div style={{ fontSize: 12, color: '#868c9a' }}>{permissions.project.name}</div>
              </div>
              <button style={topButtonStyle()} onClick={() => setPermissions(null)}>Close</button>
            </div>
            <div style={{ fontSize: 12, color: '#4c5360', lineHeight: 1.5 }}>
              Each row grants a user or group access to this project. Set <strong>View</strong>, <strong>Edit</strong>, or <strong>Manage</strong> to <strong>Allow</strong> or <strong>Deny</strong> — leave a column on <strong>Unspecified</strong> to inherit from the person’s site role. <strong>Deny</strong> always overrides <strong>Allow</strong>. Check <strong>Project leader</strong> to grant full control in one step.
            </div>
            {permissions.error ? <div style={{ fontSize: 12, color: '#c0492f' }}>{permissions.error}</div> : null}
            <div style={{ display: 'grid', gap: 10 }}>
              {permissions.rules.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 120px 120px 120px auto', gap: 8, alignItems: 'center', padding: '0 13px', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: '#868c9a' }}>
                  <div>Type</div>
                  <div>User / group</div>
                  <div title="See the project and open its workbooks">View</div>
                  <div title="Change content inside the project">Edit</div>
                  <div title="Full control: rename, move, and set project permissions">Manage</div>
                  <button aria-hidden="true" tabIndex={-1} style={{ ...topButtonStyle(), visibility: 'hidden' }}>Remove</button>
                </div>
              ) : null}
              {permissions.rules.map((rule, index) => (
                <div key={rule.id || `${rule.subject_kind}:${rule.subject_id}:${index}`} style={{ ...adminRowStyle, padding: 12, display: 'grid', gap: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 120px 120px 120px auto', gap: 8, alignItems: 'center' }}>
                    <select
                      value={rule.subject_kind}
                      onChange={(e) => setPermissions((prev) => prev ? ({
                        ...prev,
                        rules: prev.rules.map((item, itemIndex) => itemIndex === index ? { ...item, subject_kind: e.target.value } : item),
                      }) : prev)}
                    >
                      <option value="user">User</option>
                      <option value="group">Group</option>
                    </select>
                    <select
                      value={rule.subject_id}
                      onChange={(e) => setPermissions((prev) => prev ? ({
                        ...prev,
                        rules: prev.rules.map((item, itemIndex) => itemIndex === index ? { ...item, subject_id: e.target.value } : item),
                      }) : prev)}
                    >
                      <option value="">Select…</option>
                      {(rule.subject_kind === 'group' ? siteGroups.map((group) => ({ id: group.id, label: group.name })) : (siteUsers || []).map((member) => ({ id: member.user_id, label: member.email }))).map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    {['view', 'edit', 'manage'].map((capability) => (
                      <select
                        key={capability}
                        title={capability === 'view' ? 'View: see the project and open its workbooks' : capability === 'edit' ? 'Edit: change content inside the project' : 'Manage: full control — rename, move, and set project permissions'}
                        value={String((rule.capabilities || {})[capability] || '')}
                        onChange={(e) => setPermissions((prev) => {
                          if (!prev) return prev
                          return {
                            ...prev,
                            rules: prev.rules.map((item, itemIndex) => {
                              if (itemIndex !== index) return item
                              const nextCapabilities = { ...(item.capabilities || {}) }
                              const nextValue = String(e.target.value || '').trim()
                              if (nextValue) nextCapabilities[capability] = nextValue
                              else delete nextCapabilities[capability]
                              return {
                                ...item,
                                capabilities: nextCapabilities,
                              }
                            }),
                          }
                        })}
                      >
                        <option value="">Unspecified</option>
                        <option value="allow">Allow</option>
                        <option value="deny">Deny</option>
                      </select>
                    ))}
                    <button
                      style={topButtonStyle()}
                      onClick={() => setPermissions((prev) => prev ? ({
                        ...prev,
                        rules: prev.rules.filter((_, itemIndex) => itemIndex !== index),
                      }) : prev)}
                    >
                      Remove
                    </button>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#4c5360' }}>
                    <input
                      type="checkbox"
                      checked={!!rule.is_project_leader}
                      onChange={(e) => setPermissions((prev) => prev ? ({
                        ...prev,
                        rules: prev.rules.map((item, itemIndex) => itemIndex === index ? { ...item, is_project_leader: e.target.checked } : item),
                      }) : prev)}
                    />
                    Project leader
                  </label>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <button
                style={topButtonStyle()}
                onClick={() => setPermissions((prev) => prev ? ({
                  ...prev,
                  rules: [...prev.rules, {
                    id: `draft-${Date.now()}`,
                    project_id: prev.project.id,
                    subject_kind: 'group',
                    subject_id: '',
                    capabilities: {},
                    is_project_leader: false,
                  }],
                }) : prev)}
              >
                Add Rule
              </button>
              <button style={topButtonStyle(true)} onClick={() => void saveProjectPermissions()} disabled={permissions.saving}>
                {permissions.saving ? 'Saving…' : 'Save Permissions'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {renderSecurityPanel()}
      {busy ? <div style={{ position: 'fixed', right: 16, bottom: 16, fontSize: 12, background: '#1c1f26', color: '#fff', borderRadius: 999, padding: '8px 12px' }}>Loading…</div> : null}
    </FuseGridSurfaceShell>
  )
}
